/**
 * @file MeetingDetailView.tsx
 * @brief Presentational core for a single MeetingIntel meeting (scope-agnostic).
 *
 * @details
 * The immersive detail body — back link, header, analytics score strip, tabbed
 * content (Summary / Transcript / Action Items / Decisions / Mindmap), and the
 * right rail (recording, participants, bookmarks). Layout uses GridBlock flush
 * to the content rail (29-07-2026). Summary / Action items / Decisions / Key
 * points tab bodies are flush GridBand + GridCell rows too (04-08-2026).
 * Speaker resolve, while a meeting is awaiting names, is the same flush
 * grid (25-09-2026).
 * Transcript uses the mobile outline chat bubbles (TranscriptBubbleTurn,
 * 30-09-2026; was a centre-spine timeline).
 * Data fetching + loading / error live in the thin page wrappers
 * (`CompanyMeetingDetailPage`, `AdminMeetingDetailPage`); this component just
 * renders a loaded meeting.
 *
 * `scope` is forwarded to `MeetingRowActions` so the manage menu hits the right
 * endpoints. `attribution` lets the super-admin view inject the owner + company
 * line into the header (the company view passes nothing).
 *
 * The Mindmap tab has two views (04-08-2026): a React Flow canvas
 * (`MeetingMindmapCanvas`) as the default and the original indented list as the
 * alternate. Both read `mindmapGraph.ts`, which now owns the tree builder and
 * the per-type visuals that used to live in this file.
 *
 * Icons are the mobile Meeting Details set, not web-local picks (02-08-2026) —
 * see the `TABS` comment. Lucide's legacy aliases (`AlertCircle`,
 * `AlertTriangle`, `CheckCircle2`, `CheckSquare`) were swapped for the current
 * names mobile imports (`CircleAlert`, `TriangleAlert`, `CircleCheck`,
 * `SquareCheck`) — same glyphs, but now a grep for an icon finds both clients.
 *
 * @dependencies
 * - components/ui/{Button, AudioPlayer, Avatar}
 * - features/analytics/components/GridBlock
 * - features/company-meetings/components/{MeetingScoreStrip, MeetingStatusBadge, MeetingRowActions}
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/* (mobile parity)
 *
 * @author DES
 * @date 10-07-2026
 * @updated 05-08-2026 — Add to-do composer uses panel DatePicker + Select
 */

import { FAILURE_COPY, failureReasonOf } from "../meetingFailure";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  Bookmark,
  Check,
  Square,
  SquareCheck,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleAlert,
  CircleCheck,
  Clock,
  FileText,
  Info,
  CircleDashed,
  CircleSlash,
  Lightbulb,
  ListChecks,
  Lock,
  Eye,
  EyeOff,
  Mic,
  Paperclip,
  PenLine,
  X,
  LoaderCircle,
  type LucideIcon,
  MessageSquareText,
  Search,
  Plus,
  Send,
  Share2,
  Sparkles,
  TriangleAlert,
  Users,
  Volume1,
  Waypoints,
  Pencil,
  UserPen,
  Trash2,
  UserRoundPen,
  UserCheck,
  ShieldCheck,
  ArrowDownToLine,
  Tag,
} from "lucide-react";
import { TodoTick } from "@/components/icons/TodoTick";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { TagsMenu } from "@/features/notes/TagsMenu";
import {
  MeetingShareMenu,
  useMeetingAccess,
} from "@/features/employee-boardroom/meetingListTools";
import { useMeetingExport } from "@/shared/auth/meExtras";
import { AskSpark } from "@/components/icons/AskSpark";
import { AskPalette } from "./AskPalette";
import { AskMeetingsPanel } from "./AskMeetingsPanel";
import { useSaveMeetingTags } from "@/features/employee-boardroom/meetingListLogic";
import { noteDockGlass } from "@/features/notes/noteFormat";
import { pointLabels, readPointLevels } from "@/features/notes/pointLevels";
import { dockFade, dockSelected } from "@/shared/theme/dockGlass";
import { PhoneMindmapTree } from "./PhoneMindmapTree";
import {
  TranscriptEditSheet,
  type TranscriptEditMode,
  type TranscriptSelection,
} from "./TranscriptEditSheet";
import { useConnectorStatus, usePushMeetingToAsana } from "@/features/connectors/api";
import {
  useAssignablePeople,
  useDeleteMeetingTodo,
  useMeetingTagCatalogue,
  useRegenerateCompanyMeetingIntelligence,
  useReprocessCompanyMeetingDiarization,
  useDismissMeetingTodo,
  useSetTodoFollowUp,
} from "../api";
import { isExternalAssignee } from "../todoFollowUp";
import { EvidenceCollapse, MeetingEvidenceInline } from "./MeetingEvidenceInline";
import { useEvidenceOpen } from "./evidenceOpen";
import { shapeAgendaCheck } from "./agendaCheckView";
import { TRANSCRIPT_SCRUBBER_MIN_TURNS, TranscriptScrubber } from "./TranscriptScrubber";
import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import {
  useRegenerateAdminMeetingIntelligence,
  useReprocessAdminMeetingDiarization,
} from "@/features/admin-meetings/api";
import { apiClient } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import { activeTodos, isDismissedTodo } from "../todoDismiss";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Avatar } from "@/components/ui/Avatar";
import { TranscriptBubbleTurn } from "@/components/ui/TranscriptBubbleTurn";
import { useMainScroll } from "@/shared/scroll/main-scroll-context";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { AudioPlayBadge, AudioPlayerCard } from "@/components/ui/AudioPlayer";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  formatDate,
  formatDateTime,
  formatDurationSeconds,
  getInitials,
  isRTLText,
} from "@/shared/utils/formatters";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { useAddGuestEmail, useExportCompanyMeetingMom, useToggleMeetingTodo, useMeetingAttachedNotes } from "../api";
// The meeting's side of an ADR-023 link. Import direction is one-way:
// company-meetings/api knows nothing about notes.
import { useDetachNoteFromMeeting, useSetNoteMeetingShared } from "@/features/notes/api";
import { MeetingNotePicker } from "@/features/notes/MeetingNotePicker";
import { useNotesBasePath } from "@/features/notes/useNotesBasePath";
import { Link, useLocation } from "react-router-dom";
import { toast } from "@/shared/hooks/useToast";
import { attachedNoteAuthorName, isNotesTierMeeting } from "../schemas";
import type {
  MeetingAttachedNote,
  MeetingDetail,
  MeetingEvidenceAnchor,
  MeetingResolvedSpeaker,
  MeetingTodo,
  MeetingTranscriptRow,
} from "../schemas";
import { groupKeyPoints } from "../keyPointGroups";
import { INTEL_DEFAULT_ORDER, placeKeyed, pointsSectionKeys } from "../intelLayout";
import { useIntelLayout, useTabLayout, type TabLayoutControl } from "../useIntelLayout";
import { sortUnitAttrs, useSectionSort } from "../useSectionSort";
import { IntelLayoutFrame, IntelLayoutHeading, SectionChevron } from "./IntelLayoutHeading";
import { MeetingNotesTierView } from "./MeetingNotesTierView";
import { hasNotesPreviewFlag, previewNotesDocFor } from "../notesDoc";
import { GridHeading } from "@/components/ui/GridHeading";
import {
  MEETING_GRID_HEADINGS,
  MEETING_OLD_HEADING_CLS,
  MeetingBlockHeading,
} from "./MeetingBlockHeading";
import {
  meetingBodyLeading,
  participantNameTerms,
  renderNameHighlights,
} from "./meetingNameHighlight";
import { MeetingScoreStrip } from "./MeetingScoreStrip";
import { MeetingStatusBadge } from "./MeetingStatusBadge";
import { MeetingRowActions, type MeetingActionsScope } from "./MeetingRowActions";
import { MeetingMindmapCanvas } from "./MeetingMindmapCanvas";
import { MeetingMindmapEvidence } from "./MeetingMindmapEvidence";
import { MeetingRenameDialog } from "./MeetingRenameDialog";
import { MeetingExportSheet } from "./MeetingExportSheet";
import { useShareOneItem } from "../exportPickApi";
import { deferAfterDropdownMenu } from "@/shared/utils/restoreBodyInteraction";
import { AddPersonForm } from "@/features/employee-boardroom/AddPersonForm";
import { TodoFormSheet } from "./TodoFormSheet";
import { TodoGroupsBody } from "./TodoGroupsBody";
import { buildMindmapEvidenceIndex, resolveMindmapEvidence } from "./mindmapEvidence";
import {
  anchorClipSecs,
  anchorLabel,
  buildSegmentStarts,
  formatClock,
  pairDecisions,
  resolveVoiceAnchor,
  splitRiskLines,
  type PairedDecision,
  type VoiceAnchor,
} from "./meetingEvidence";
import {
  branchIds,
  buildMindmapTree,
  defaultExpandedIds,
  mindmapVisual,
  type MindmapTreeNode,
} from "./mindmapGraph";
import { useMeetingMomLanguage } from "../hooks/useMeetingMomLanguage";
import { SpeakerSwapControl, type SwapAudit } from "./SpeakerSwapControl";
import { EmployeeSpeakerResolvePanel } from "@/features/employee-boardroom/EmployeeSpeakerResolvePanel";

// ----------------------------------------------------------------------
// Tab model
// ----------------------------------------------------------------------
type DetailTab =
  | "summary"
  | "transcript"
  | "todos"
  | "decisions"
  | "keypoints"
  | "coverage"
  | "mindmap";

// Glyphs are the mobile Meeting Details dock's, not web-local picks — the same
// idea must not wear two icons across the two clients (02-08-2026). Mobile
// source: FRONTEND/.../MeetingDetails/hooks/useMeetingDetailsContent.ts
// (`meetingDetailSections`), which carries the rationale for each choice.
//   Summary   -> Sparkles           (mobile "Intel" — the AI-generated read)
//   Todos     -> ListChecks         (mobile "Tasks" — a literal checklist)
//   Keypoints -> Lightbulb          (mobile "Points" — deliberately NOT a list)
//   Mindmap   -> Waypoints          (mobile "Map" — a node graph, not Network,
//                                    which reads as infrastructure)
//   Transcript-> MessageSquareText  (mobile "Script" — a spoken exchange as text;
//                                    last in the web row, 29-09-2026)
// `decisions` has no mobile dock entry — mobile keeps decisions inside Intel —
// so it takes the glyph mobile puts on the "Key decisions" rows themselves
// (CircleCheck), NOT a warning triangle: the tab is the settled outcomes, and
// the risks half of it already carries its own CircleAlert callout.
const TABS: Array<{ id: DetailTab; label: string; icon: LucideIcon }> = [
  { id: "summary", label: "Summary", icon: Sparkles },
  { id: "todos", label: "Action Items", icon: TodoTick },
  { id: "decisions", label: "Decisions", icon: CircleCheck },
  { id: "keypoints", label: "Key points", icon: Lightbulb },
  // Agenda is CONDITIONAL — filtered out unless the meeting carries coverage,
  // which the API returns only to the owner. That presence check is therefore
  // the visibility rule too; this file must not re-derive it. Glyph mirrors
  // mobile's Agenda dock entry (ClipboardCheck). (19-08-2026)
  { id: "coverage", label: "Agenda", icon: AgendaFlag }, // DES 08-10-2026: app icon, was ClipboardCheck
  { id: "mindmap", label: "Mindmap", icon: Waypoints },
  // Transcript last (29-09-2026, DES) — intelligence tabs first, raw script at
  // the end of the row.
  { id: "transcript", label: "Transcript", icon: MessageSquareText },
];

// ----------------------------------------------------------------------
// Speaker id → display name resolver (transcript + todo assignees)
// ----------------------------------------------------------------------
type SpeakerMeta = {
  name: string;
  /** Account email, when known — the key ToDo assignees resolve on. */
  email?: string | null;
  profileImageUrl?: string | null;
  isExternal: boolean;
};

// Photos can sit on `resolved_speakers` OR only on the meeting's `participants`
// roster — the two arrays are populated by different paths, and the Participants
// panel reads the roster, which is why it can show a face for someone the
// transcript shows as initials. So the roster is consulted as a fallback, keyed
// **by email first, then normalized name**.
//
// ⛔ Never pair the two arrays by ARRAY INDEX: `resolved_speakers` is in
// Deepgram track order and `participants` is the order the host added people at
// setup. They are unrelated — on one live meeting they were exactly reversed,
// which took the NAME from one person and the PHOTO from another. Falling back
// to initials is always better than pinning the wrong face to a speaker.
// (03-08-2026) Related: docs/product/meeting-intelligence.md
function buildSpeakerMetaMap(
  speakers?: MeetingResolvedSpeaker[] | null,
  participants?: MeetingDetail["participants"] | null,
): Map<string, SpeakerMeta> {
  const normName = (v: unknown) =>
    String(v ?? "")
      .toLowerCase()
      .replace(/\(client\)/gi, "")
      .replace(/\s+/g, " ")
      .trim();

  const rosterByEmail = new Map<string, string>();
  const rosterByName = new Map<string, string>();
  for (const p of participants ?? []) {
    const rawPhoto = p?.profileImageUrl ?? p?.profile_image_url ?? null;
    // Roster rows come through a passthrough schema, so narrow before trusting.
    const photo = typeof rawPhoto === "string" && rawPhoto.trim() ? rawPhoto : null;
    if (!photo) continue;
    const email = String(p?.email ?? "")
      .toLowerCase()
      .trim();
    if (email && !rosterByEmail.has(email)) rosterByEmail.set(email, photo);
    const name = normName(p?.name);
    if (name && !rosterByName.has(name)) rosterByName.set(name, photo);
  }

  const map = new Map<string, SpeakerMeta>();
  for (const s of speakers ?? []) {
    const id = s.speakerId ?? s.speaker_id ?? undefined;
    if (!id) continue;
    const name = s.resolvedUser?.name ?? s.name ?? id;
    const email = String(s.email ?? s.resolvedUser?.email ?? "")
      .toLowerCase()
      .trim();
    const profileImageUrl =
      s.profileImageUrl ??
      s.profile_image_url ??
      s.resolvedUser?.profileImageUrl ??
      (email ? rosterByEmail.get(email) : undefined) ??
      rosterByName.get(normName(name)) ??
      null;
    const isExternal = Boolean(s.isExternal ?? s.is_external ?? s.resolvedUser?.isExternal);
    map.set(id, { name, email, profileImageUrl, isExternal });
  }
  return map;
}

const UNASSIGNED_ASSIGNEE: SpeakerMeta = {
  name: "Unassigned",
  isExternal: false,
  profileImageUrl: null,
};

/**
 * Resolve a ToDo's assignee to a person we can show a face for.
 *
 * **Email first.** `assigneeSpeakerId` comes from the synthesis LLM and can name
 * a track that does not exist (live case: `speaker_2` on a two-track meeting),
 * whereas `assigneeEmail` is stamped deterministically server-side from the
 * resolved roster. Keying on the speaker id alone used to fall back to
 * `speaker_0` and attribute the task's NAME and AVATAR to whoever happened to be
 * track 0. Mirrors `resolveAssignee` in the mobile
 * `useMeetingDetailsContent.ts`. (03-08-2026)
 */
function resolveTodoAssignee(
  todo: MeetingTodo,
  speakers: Map<string, SpeakerMeta>,
  participants?: MeetingDetail["participants"] | null,
): SpeakerMeta {
  // "Team": a collective "we" task, or one the owner gave to the group. Before
  // the email lookup, which it has none for. Mirrors mobile. (28-09-2026)
  if (todo.assigneeSpeakerId === "team") {
    return { name: "Team", isExternal: false, profileImageUrl: null };
  }
  const email = String((todo as { assigneeEmail?: string }).assigneeEmail ?? "")
    .toLowerCase()
    .trim();
  if (email) {
    for (const meta of speakers.values()) {
      if (meta.email && meta.email === email) return meta;
    }
    // An assignee who was on the roster but never matched to a diarized track
    // still has a name and a face — mobile's assigneesByEmail spans both lists.
    for (const p of participants ?? []) {
      if (
        String(p?.email ?? "")
          .toLowerCase()
          .trim() !== email
      )
        continue;
      const photo = p?.profileImageUrl ?? p?.profile_image_url ?? null;
      return {
        name: String(p?.name ?? "").trim() || email,
        email,
        profileImageUrl: typeof photo === "string" ? photo : null,
        isExternal: Boolean(p?.isExternal),
      };
    }
  }

  const bySpeaker = todo.assigneeSpeakerId ? speakers.get(todo.assigneeSpeakerId) : undefined;
  if (bySpeaker) return bySpeaker;

  // Backend stamped a name the roster cannot match — never borrow someone
  // else's photo, but do use the assignee's own if the API signed one. That is
  // the normal case for a MANUAL to-do: its assignee may be any company member,
  // so the meeting roster will never hold them. (05-08-2026)
  const name = String((todo as { assigneeName?: string }).assigneeName ?? "").trim();
  if (name) {
    return {
      name,
      isExternal: false,
      profileImageUrl: (todo as { assigneeAvatarUrl?: string | null }).assigneeAvatarUrl ?? null,
    };
  }

  return UNASSIGNED_ASSIGNEE;
}

// ----------------------------------------------------------------------
// Tab: Summary
// Flush GridBlock brief (04-08-2026): Overview / Executive summary / At a
// glance use the same hairline cells as MeetingScoreStrip + tab chrome.
// ----------------------------------------------------------------------
function splitProseParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// ----------------------------------------------------------------------
// Attached notes — the meeting side of an ADR-023 link (21-08-2026)
//
// Deliberately a SECTION at the end of Summary rather than a tab: a tab would
// sit in the dock for every meeting and be empty for nearly all of them.
//
// It shipped read-only, on the grounds that the web had no notes library to
// attach from and a detach on a surface that cannot re-attach is a one-way
// door. Notes now exist on the web, so that condition has expired and the
// section attaches, opens and removes — the reasoning changed because the fact
// it rested on did. (21-08-2026)
//
// Under `/admin` there is no Notes surface, and a super admin is never a
// meeting's owner anyway, so the base path is null there and the section
// renders as plain text with no links and no controls.
// Related: FRONTEND/.../MeetingDetails/components/MeetingAttachedNotes.tsx
// ----------------------------------------------------------------------
/**
 * A note's points — the same precedence `listMeetingAgendaPoints` uses.
 *
 * `points` first, and a TYPED note falls back to `transcript_text` because that
 * is where its lines lived before ADR-025 gave points a column. A recording
 * gets NO fallback: its transcript is not a list, and splitting it on newlines
 * produced one unbounded pseudo-point that rendered as a wall of prose where
 * the points belong. (24-08-2026)
 */
function attachedNotePoints(note: MeetingAttachedNote): string[] {
  const source =
    note.points && note.points.trim()
      ? note.points
      : note.source === "typed"
        ? (note.transcript_text ?? "")
        : "";
  return String(source)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function AttachedNotesSection({
  meetingId,
  notes,
  isMeetingOwner,
  scope,
  layoutCtl,
}: {
  meetingId: string;
  notes: MeetingAttachedNote[];
  /** U4: the Intel tab's layout. Given = Agenda and Private notes collapse,
   *  and the card's headings drag / have the Move menu (the card moves as one). */
  layoutCtl?: TabLayoutControl;
  /** Which shell this meeting is being read in. `admin` is oversight. */
  scope: MeetingActionsScope;
  /** True when the viewer recorded this meeting. Decides who may remove a
   *  contribution — the author always may, the meeting's owner may too — and
   *  whether attaching asks about visibility. */
  isMeetingOwner: boolean;
  /** Unused since 08-10-2026: the section stays LTR in RTL content (DES,
   *  only the AI text reads RTL). Kept so callers need not change. */
  contentRtl?: boolean;
}) {
  const base = useNotesBasePath();
  const location = useLocation();
  const detach = useDetachNoteFromMeeting();
  const setShared = useSetNoteMeetingShared();
  const { user } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Which contribution is open.
   *
   * Someone else's note has no page to send you to — `GET /notes/:id` is
   * owner-only — but SHARING has to mean the room can read it, not merely that
   * a row appears. The full text already arrives with the meeting's attached
   * notes, so opening one is an expand, not a fetch.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const viewerId = user?.id ?? null;
  // NOT `base !== null` any more. Every shell has a Notes area now, including
  // the super admin's — but a super admin browsing someone else's meeting is
  // an overseer, not a participant, and the server refuses an attach from one
  // (the visibility predicate carries no admin branch). Offering the control
  // there would be a button that always fails. (23-08-2026)
  const canManage = scope !== "admin";

  // Nothing attached AND nowhere to attach from — draw nothing. Where Notes
  // does exist the empty state earns its space: it is how an agenda gets
  // attached before the meeting is processed.
  if (notes.length === 0 && !canManage) {
    return null;
  }

  // `after` is the specific case and `agenda` is everything else — never the
  // other way round, or a link with an unrecognised role would fall into
  // neither block and vanish from the meeting. ADR-023 keeps `role` open so it
  // can grow. A note in the wrong block is a mislabel; a note in no block is a
  // lost note.
  const after = notes.filter((n) => n.role === "addendum");
  const agenda = notes.filter((n) => n.role !== "addendum");
  // Counted by PEOPLE as well as notes: "6 notes" alone does not say whether
  // four colleagues wrote something or one person wrote six.
  const authorCount = new Set(after.map((n) => n.owner_id ?? n.id)).size;
  const notesShut = MEETING_GRID_HEADINGS && !!layoutCtl?.isCollapsed("notes");

  const renderRow = (note: MeetingAttachedNote) => {
    const points = attachedNotePoints(note);
    // Sub-points (DES 12-10-2026): lettered and indented under their point.
    const levels = readPointLevels(note.point_levels, points.length);
    const labels = pointLabels(levels, points.length);
    const isTyped = note.source === "typed";
    const Glyph = isTyped ? PenLine : Mic;
    // Points if it has them, whichever kind it is — a recording has had them
    // since ADR-025, and "voice note" said less than "4 points" does. The
    // fallback still covers a recording made before points existed.
    const size =
      points.length > 0
        ? `${points.length} point${points.length === 1 ? "" : "s"}`
        : isTyped
          ? "no points"
          : "voice note";
    const body = points.join("  ·  ");
    const isMine = !!viewerId && note.owner_id === viewerId;
    const author = isMine ? "You" : attachedNoteAuthorName(note);
    const expanded = expandedId === note.id;

    const inner = (
      <div
        className="flex items-start gap-3 px-4 py-3"
        // Held LTR in RTL content too: only the AI text reads RTL (DES
        // 08-10-2026, "notes agendas ko rtl ki zaroorat nahin hai").
        dir="ltr"
      >
        {/* Photo whenever there is one — the rule the rest of boardroom
            follows. `isExternal` gates here are what once blanked real
            people's faces. */}
        <Avatar
          src={note.author_image_url}
          initials={getInitials(attachedNoteAuthorName(note))}
          alt={attachedNoteAuthorName(note)}
          size="sm"
          className="mt-0.5 shrink-0"
        />
        <div className="min-w-0 flex-1">
          {/* `truncate` has to sit on the TEXT, not on the flex row: on a
              flex container the text becomes an anonymous flex item and the
              ellipsis never applies, so a long title overflowed its column. */}
          <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-foreground">
            <Glyph className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span className="truncate">{note.title?.trim() || "Untitled note"}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {author} · {size}
          </p>
          {/* Open, the points are a numbered list — the shape they were
              written in. Closed, they collapse to two lines so ten
              contributions still fit on a screen. */}
          {expanded && points.length > 0 ? (
            <ol className="mt-1.5 space-y-1">
              {points.map((point, i) => (
                <li key={i} className={cn("flex gap-2 text-sm leading-6", levels?.[i] ? "pl-6" : "")}>
                  <span className="w-4 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
                    {labels[i]}.
                  </span>
                  <span className="min-w-0 text-foreground/90">{point}</span>
                </li>
              ))}
            </ol>
          ) : body ? (
            <p className="mt-1 line-clamp-2 text-sm text-foreground/90">{body}</p>
          ) : null}
          {/* Yours to publish or withdraw, and stated either way — someone who
              assumes the room has read their point will not say it out loud
              again, so "only you" has to be visible rather than inferred. */}
          {isMine ? (
            <button
              type="button"
              disabled={setShared.isPending}
              className="mt-1 flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-brand"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShared.mutate(
                  {
                    meetingId,
                    noteId: note.id,
                    shared: note.shared !== true,
                  },
                  {
                    onError: () =>
                      toast({
                        variant: "destructive",
                        title: "Could not change",
                        description: "Please try again.",
                      }),
                  },
                );
              }}
            >
              {note.shared ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {note.shared ? "Everyone can see this" : "Only you can see this"}
              {/* The ACTION carries its own glyph, separate from the state one
                  beside it: one says what is true now, the other what clicking
                  does. `Share2` is the share mark both clients already use, so
                  the same name draws the same thing. */}
              <span className="inline-flex items-center gap-1 font-semibold text-brand">
                {note.shared ? <Lock className="h-3 w-3" /> : <Share2 className="h-3 w-3" />}
                {note.shared ? "Make private" : "Share"}
              </span>
            </button>
          ) : null}
        </div>
        {/* Offered only to the two people the server actually allows: the
            note's author, and the meeting's owner. */}
        {canManage && (isMine || isMeetingOwner) ? (
          <button
            type="button"
            aria-label={`Remove ${note.title?.trim() || "note"}`}
            title="Remove from this meeting"
            className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              // The row is a link — without this, removing also navigates to
              // the note being removed.
              e.preventDefault();
              e.stopPropagation();
              detach.mutate(
                { meetingId, noteId: note.id },
                {
                  onError: () =>
                    toast({
                      variant: "destructive",
                      title: "Could not remove",
                      description: "Please try again.",
                    }),
                },
              );
            }}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );

    return (
      <GridCell key={note.id} padded={false}>
        {canManage && isMine ? (
          <Link
            to={`${base}/notes/${note.id}`}
            // Back from the note returns HERE, to this meeting, not to the
            // Notes Library (DES 08-10-2026).
            state={{ backTo: `${location.pathname}${location.search}`, backLabel: "Back to meeting" }}
            className="block hover:bg-muted/40"
          >
            {inner}
          </Link>
        ) : (
          // Someone else's contribution has no page — the note belongs to
          // them — so it opens where it is instead.
          <button
            type="button"
            aria-expanded={expanded}
            className="block w-full text-left hover:bg-muted/40"
            onClick={() => setExpandedId((prev) => (prev === note.id ? null : note.id))}
          >
            {inner}
          </button>
        )}
      </GridCell>
    );
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      <SectionMeta
        stats={[
          {
            value: notes.length,
            label: notes.length === 1 ? "note" : "notes",
            tone: "brand",
          },
        ]}
      />
      {canManage ? (
        <MeetingNotePicker
          anchor={
            <Button variant="outline" size="sm">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Attach
            </Button>
          }
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          meetingId={meetingId}
          attachedNoteIds={notes.map((n) => n.id)}
          askVisibility={!isMeetingOwner}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {MEETING_GRID_HEADINGS ? (
        // The drawer heading (trial, DES 08-10-2026), the note count and
        // Attach on the line's far end.
        <GridBand>
          {layoutCtl ? (
            // U4: drags and has the Move menu, but no arrow: the card's two
            // blocks below collapse on their own headings.
            <IntelLayoutFrame ctl={layoutCtl} sectionKey="notes" label="Attached notes" collapsible={false}>
              <GridHeading label="Attached notes" dir="ltr" action={headerActions} />
            </IntelLayoutFrame>
          ) : (
            <GridHeading label="Attached notes" dir="ltr" action={headerActions} />
          )}
        </GridBand>
      ) : (
        <GridBand className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Paperclip className="h-4 w-4 text-brand" />
            Attached notes
          </h3>
          {headerActions}
        </GridBand>
      )}

      {notes.length === 0 ? (
        <GridBand>
          <p className="text-sm text-muted-foreground">
            Attach a note you wrote before this meeting and its points become the agenda — the
            intelligence then reports which were discussed.
          </p>
        </GridBand>
      ) : null}

      {/* Two blocks, never one list: the plan and the follow-ups stop being the
          same kind of thing once several people are contributing. */}
      {agenda.length > 0 ? (
        <>
          {MEETING_GRID_HEADINGS ? (
            <GridBand className="pb-1">
              {layoutCtl ? (
                <IntelLayoutHeading ctl={layoutCtl} sectionKey="agenda" label="Agenda" count={agenda.length} className="" />
              ) : (
                <GridHeading label="Agenda" dir="ltr" />
              )}
            </GridBand>
          ) : (
            <GridBand className="bg-muted/25 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Agenda
              </p>
            </GridBand>
          )}
          {MEETING_GRID_HEADINGS && layoutCtl?.isCollapsed("agenda") ? null : (
            <GridCells className="grid-cols-1">{agenda.map(renderRow)}</GridCells>
          )}
        </>
      ) : null}

      {after.length > 0 ? (
        <>
          <GridBand className={MEETING_GRID_HEADINGS ? "pb-1" : "bg-muted/25 py-2"}>
            {/* "Private notes", not "After the meeting" (02-10-2026): notes
                written in the recorder DURING the meeting land here too
                (`addendum`, so they never steer the shared intelligence). */}
            {MEETING_GRID_HEADINGS && layoutCtl ? (
              <IntelLayoutHeading
                ctl={layoutCtl}
                sectionKey="notes"
                label="Private notes"
                count={after.length}
                className=""
              />
            ) : MEETING_GRID_HEADINGS ? (
              <GridHeading label="Private notes" dir="ltr" />
            ) : (
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Private notes
              </p>
            )}
            {notesShut ? null : (
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {after.length} note{after.length === 1 ? "" : "s"} from {authorCount}{" "}
                {authorCount === 1 ? "person" : "people"}, written during or after the meeting. Not
                part of its intelligence.
              </p>
            )}
          </GridBand>
          {notesShut ? null : <GridCells className="grid-cols-1">{after.map(renderRow)}</GridCells>}
        </>
      ) : null}

    </>
  );
}

function SummaryTab({
  meeting,
  attachedNotes = [],
  scope,
  contentRtl = false,
}: {
  meeting: MeetingDetail;
  attachedNotes?: MeetingAttachedNote[];
  scope: MeetingActionsScope;
  contentRtl?: boolean;
}) {
  const overview = meeting.meeting_overview?.trim();
  const summary = (meeting.meeting_summary ?? meeting.summary_overview ?? "").trim();

  if (!overview && !summary) {
    // Attached notes still render: an agenda is attached BEFORE the meeting is
    // processed, so the state with no summary is exactly the one where seeing
    // it confirms the attach worked.
    return (
      <>
        <div className="p-4 md:p-6">
          <EmptyTab
            icon={Sparkles}
            title="No summary yet"
            hint="The summary appears once meeting intelligence has been compiled."
          />
        </div>
        <AttachedNotesSection
          meetingId={meeting.id}
          notes={attachedNotes}
          isMeetingOwner={meeting.is_owner !== false}
          scope={scope}
          contentRtl={contentRtl}
        />
      </>
    );
  }

  const overviewRtl = contentRtl || isRTLText(overview);
  const summaryParagraphs = summary ? splitProseParagraphs(summary) : [];
  const decisionCount = (meeting.key_decisions ?? []).filter(Boolean).length;
  const actionCount = activeTodos(meeting.todos).length;
  const pointCount = (meeting.key_points ?? []).filter((kp) => !!kp?.point?.trim()).length;
  const showGlance = decisionCount > 0 || actionCount > 0 || pointCount > 0;

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Sparkles className="h-4 w-4 text-brand" />
          Meeting brief
        </h3>
        <SectionMeta
          stats={[
            ...(overview ? [{ value: 1, label: "overview", tone: "brand" as const }] : []),
            ...(summary
              ? [
                  {
                    value: summaryParagraphs.length || 1,
                    label: summaryParagraphs.length === 1 ? "section" : "sections",
                    tone: "default" as const,
                  },
                ]
              : []),
          ]}
        />
      </GridBand>

      {overview ? (
        <GridBand className="bg-muted/25">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Overview
          </p>
          <p
            dir={overviewRtl ? "rtl" : "ltr"}
            className="whitespace-pre-line text-[17px] font-medium leading-8 tracking-tight text-foreground"
          >
            {overview}
          </p>
        </GridBand>
      ) : null}

      {summary ? (
        <GridBand>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2.5">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-soft text-brand">
                <FileText className="h-3.5 w-3.5" />
              </span>
              Executive summary
            </h4>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
              {summaryParagraphs.length} {summaryParagraphs.length === 1 ? "passage" : "passages"}
            </span>
          </div>
          <div className="space-y-4">
            {summaryParagraphs.map((para, i) => (
              <p
                key={i}
                dir={contentRtl || isRTLText(para) ? "rtl" : "ltr"}
                className="whitespace-pre-line text-[15px] leading-7 text-foreground/90"
              >
                {para}
              </p>
            ))}
          </div>
        </GridBand>
      ) : null}

      {showGlance ? (
        <>
          <div className="border-b border-r border-border/60 bg-muted/25 px-4 py-2.5 md:px-5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              At a glance
            </p>
          </div>
          <GridCells className="grid-cols-3">
            <GridCell>
              <p className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                {decisionCount}
              </p>
              <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">Decisions</p>
            </GridCell>
            <GridCell>
              <p className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                {actionCount}
              </p>
              <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">Action items</p>
            </GridCell>
            <GridCell>
              <p className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                {pointCount}
              </p>
              <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">Key points</p>
            </GridCell>
          </GridCells>
        </>
      ) : null}

      <AttachedNotesSection
        meetingId={meeting.id}
        notes={attachedNotes}
        isMeetingOwner={meeting.is_owner !== false}
        scope={scope}
        contentRtl={contentRtl}
      />
    </>
  );
}

// ----------------------------------------------------------------------
// Tab: Transcript
// Outline chat bubbles, the mobile look (TranscriptBubbleTurn, 30-09-2026):
// speaker A left in brand, everyone else right in grey. Replaced the
// 10-07-2026 centre-spine timeline and its per-speaker colour themes.
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Per-speaker stats for the transcript filter strip + speaking-share panel.
// spokenSecs ≈ each segment's gap to the next segment's startSecs (last →
// meeting end), capped at 120s so a long silence can't inflate a total;
// falls back to turn count when startSecs is absent. Mirrors the mobile
// MeetingTranscriptSpeakerFilter (15-07-2026).
// ----------------------------------------------------------------------
type TranscriptSpeakerStat = {
  speakerId: string;
  name: string;
  profileImageUrl?: string | null;
  isExternal: boolean;
  count: number;
  spokenSecs: number;
};

function fmtSpoken(secs: number): string {
  const t = Math.round(secs);
  if (t <= 0) return "";
  const m = Math.floor(t / 60);
  const s = t % 60;
  if (m <= 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

// ----------------------------------------------------------------------
// The phone's transcript speaker strip (MeetingTranscriptSpeakerFilter).
// DES (30-09-2026): "why these are not like mobile image stack in meeting
// transcripts". 58px "All" and speaker avatars, a red turn badge on each
// corner, the first name under. On "All" the speakers slide into an
// overlapping stack; a click spreads them, the next click picks one (a
// second click on the picked one clears). The others dim to 45%.
// ----------------------------------------------------------------------
const STRIP_AVATAR = 58;
/** A speaker's slot spread out (the 80px item plus a 4px gap) or stacked. */
const STRIP_SLOT = STRIP_AVATAR + 22 + 4;
const STRIP_STACK_STEP = 24;

function StripFace({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const usable = !!url && /^https?:\/\//.test(url) && !failed;
  return usable ? (
    <img src={url!} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-cover" />
  ) : (
    <span className="text-lg font-bold text-white">{initialsOf(name)}</span>
  );
}

function TurnBadge({ n, className }: { n: number; className?: string }) {
  return (
    <span
      className={cn(
        "absolute -right-3.5 bottom-0.5 flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-app-page bg-brand px-[5px] text-[10px] font-bold leading-none text-white transition-opacity duration-200",
        className,
      )}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

function SpeakerStrip({
  speakers,
  selectedSpeakerId,
  onSelect,
}: {
  speakers: TranscriptSpeakerStat[];
  selectedSpeakerId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(selectedSpeakerId != null);
  useEffect(() => {
    if (selectedSpeakerId != null) setExpanded(true);
  }, [selectedSpeakerId]);
  const stacked = selectedSpeakerId == null && !expanded;
  const allActive = selectedSpeakerId == null;
  const totalTurns = speakers.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="flex items-start overflow-x-auto px-4 pb-1 pt-3 [-ms-overflow-style:none] [scrollbar-width:none] md:px-5 [&::-webkit-scrollbar]:hidden">
      <button
        type="button"
        onClick={() => {
          onSelect(null);
          setExpanded(false);
        }}
        aria-pressed={allActive}
        title="All speakers"
        className={cn(
          "flex w-20 shrink-0 flex-col items-center px-1 transition-opacity",
          !allActive && "opacity-[0.45]",
        )}
      >
        <span className="relative" style={{ width: STRIP_AVATAR, height: STRIP_AVATAR }}>
          <span
            className={cn(
              "flex h-full w-full items-center justify-center rounded-full border-2 bg-black/5 dark:bg-white/[0.06]",
              allActive ? "border-brand" : "border-transparent",
            )}
          >
            <Users className={cn("h-[26px] w-[26px]", allActive ? "text-brand" : "text-app-text-secondary")} />
          </span>
          <TurnBadge n={totalTurns} />
        </span>
        <span className={cn("mt-1 text-xs", allActive ? "font-bold text-app-text" : "font-medium text-app-text-secondary")}>
          All
        </span>
      </button>

      {speakers.map((sp, index) => {
        const active = selectedSpeakerId === sp.speakerId;
        return (
          <div
            key={sp.speakerId}
            className="shrink-0 transition-[width] duration-[260ms] ease-out"
            style={{ width: stacked ? STRIP_STACK_STEP : STRIP_SLOT, zIndex: speakers.length - index }}
          >
            <button
              type="button"
              onClick={() => {
                if (stacked) {
                  // The stack opens first; picking is the next click.
                  setExpanded(true);
                  return;
                }
                onSelect(active ? null : sp.speakerId);
              }}
              aria-pressed={active}
              aria-label={stacked ? "Show speakers" : `Filter by ${sp.name}`}
              title={sp.name}
              className={cn(
                "ml-1 flex w-20 flex-col items-center px-1 transition-opacity",
                !active && !allActive && "opacity-[0.45]",
              )}
            >
              <span className="relative" style={{ width: STRIP_AVATAR, height: STRIP_AVATAR }}>
                <span
                  className={cn(
                    "flex h-full w-full items-center justify-center overflow-hidden rounded-full border-2",
                    sp.profileImageUrl ? "bg-app-card" : "bg-app-text dark:bg-app-quaternary-dark",
                    active ? "border-brand" : stacked ? "border-app-page" : "border-transparent",
                  )}
                >
                  <StripFace url={sp.profileImageUrl} name={sp.name} />
                </span>
                <TurnBadge n={sp.count} className={stacked ? "opacity-0" : "opacity-100"} />
              </span>
              <span
                className={cn(
                  "mt-1 max-w-[70px] truncate text-xs transition-opacity duration-200",
                  active ? "font-bold text-app-text" : "font-medium text-app-text-secondary",
                  stacked ? "opacity-0" : "opacity-100",
                )}
              >
                {firstNameOf(sp.name)}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function buildTranscriptSpeakerStats(
  rows: MeetingTranscriptRow[],
  speakers: Map<string, SpeakerMeta>,
  totalDuration: number,
): TranscriptSpeakerStat[] {
  const seen = new Map<string, TranscriptSpeakerStat>();
  const n = rows.length;
  for (let i = 0; i < n; i++) {
    const row = rows[i]!;
    const id = row.speakerId ?? "";
    if (!id) continue;

    let dur = 0;
    const start = typeof row.startSecs === "number" ? row.startSecs : undefined;
    if (typeof start === "number") {
      const next = rows[i + 1]?.startSecs;
      if (typeof next === "number" && next > start) {
        dur = next - start;
      } else if (i === n - 1 && totalDuration > start) {
        dur = totalDuration - start;
      }
      if (dur > 0) dur = Math.min(dur, 120);
    }

    const existing = seen.get(id);
    if (existing) {
      existing.count += 1;
      existing.spokenSecs += dur;
    } else {
      const meta = speakers.get(id);
      seen.set(id, {
        speakerId: id,
        name: row.speaker?.trim() || meta?.name || id,
        profileImageUrl: row.profileImageUrl ?? meta?.profileImageUrl ?? null,
        isExternal: row.isExternal ?? meta?.isExternal ?? false,
        count: 1,
        spokenSecs: dur,
      });
    }
  }
  return Array.from(seen.values());
}

function firstNameOf(name: string): string {
  return name.split(/\s+/)[0] || name;
}

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// The search term marked inside each line (DES 05-10-2026: "search karnay
// per words highlight hotay hi nahin"). Case-insensitive, every occurrence,
// the original casing kept. The edit selection measures offsets over the
// paragraph's text, so wrapping matches in <mark> does not move them.
function highlightMatches(text: string, needle: string): ReactNode {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const out: ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(needle);
  while (at >= 0) {
    if (at > from) out.push(text.slice(from, at));
    out.push(
      <mark key={at} className="rounded bg-brand/25 px-0.5 text-foreground">
        {text.slice(at, at + needle.length)}
      </mark>,
    );
    from = at + needle.length;
    at = lower.indexOf(needle, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

export function TranscriptTab({
  rows,
  speakers,
  totalDuration,
  editable = false,
  onEdit,
  swap,
}: {
  rows: MeetingTranscriptRow[];
  speakers: Map<string, SpeakerMeta>;
  totalDuration: number;
  /** Owner, ready meeting: selections can be edited. The transcript is always
   *  the original spoken language (DES 15-10-2026, ADR-037). */
  editable?: boolean;
  onEdit?: (mode: TranscriptEditMode, selection: TranscriptSelection) => void;
  /** Change a whole speaker (02-10-2026): owner / company admin, ready meeting. */
  swap?: { meetingId: string; canSwap: boolean; swaps: SwapAudit[]; undoId: string | null };
}) {
  const [q, setQ] = useState("");
  // The turn list, for the fast-scroll handle (05-10-2026).
  const turnListRef = useRef<HTMLOListElement>(null);
  // ------------------------------------------------------------------
  // Selection → "Correct" / "Who said this?" (29-09-2026, web parity with
  // the app's press-and-hold dock). A selection counts only when it lies
  // inside ONE line's text; its offset is measured in that line's stored
  // text (the <p> renders it verbatim), which is what the routes address.
  // ------------------------------------------------------------------
  const [picked, setPicked] = useState<(TranscriptSelection & { x: number; y: number }) | null>(
    null,
  );
  const readSelection = useCallback(() => {
    if (!editable) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setPicked(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const node = range.commonAncestorContainer;
    const el = (
      node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element)
    )?.closest?.("[data-segment-id]") as HTMLElement | null;
    const segmentId = el?.dataset.segmentId;
    if (
      !el ||
      !segmentId ||
      !el.contains(range.startContainer) ||
      !el.contains(range.endContainer)
    ) {
      setPicked(null);
      return;
    }
    const raw = range.toString();
    const text = raw.trim();
    if (!text) {
      setPicked(null);
      return;
    }
    const pre = document.createRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.startContainer, range.startOffset);
    const start = pre.toString().length + (raw.length - raw.trimStart().length);
    const rect = range.getBoundingClientRect();
    setPicked({
      segmentId,
      speakerId: el.dataset.speakerId ?? "",
      text,
      start,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  }, [editable]);
  // A fixed toolbar would drift from its text on scroll: drop it instead.
  useEffect(() => {
    if (!picked) return;
    const clear = () => setPicked(null);
    window.addEventListener("scroll", clear, true);
    return () => window.removeEventListener("scroll", clear, true);
  }, [picked]);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | null>(null);
  // Cap speaking-share bars for large meetings; expand reveals everyone (16-07-2026).
  const [shareExpanded, setShareExpanded] = useState(false);

  const speakerStats = useMemo(
    () => buildTranscriptSpeakerStats(rows, speakers, totalDuration),
    [rows, speakers, totalDuration],
  );

  // Drop a stale selection if that speaker vanished (e.g. script conversion).
  useEffect(() => {
    if (selectedSpeakerId && !speakerStats.some((s) => s.speakerId === selectedSpeakerId)) {
      setSelectedSpeakerId(null);
    }
  }, [speakerStats, selectedSpeakerId]);

  // First distinct speaker → left (A); next → right (B); further speakers
  // keep alternating.
  const isAById = useMemo(() => {
    const order = new Map<string, boolean>();
    for (const r of rows) {
      const id = r.speakerId ?? r.speaker ?? "";
      if (id && !order.has(id)) order.set(id, order.size % 2 === 0);
    }
    return order;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={MessageSquareText}
        title="No transcript"
        hint="Transcription hasn't produced any dialogue for this meeting yet."
      />
    );
  }

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (selectedSpeakerId && (r.speakerId ?? "") !== selectedSpeakerId) {
      return false;
    }
    if (needle && !(r.text ?? "").toLowerCase().includes(needle)) {
      return false;
    }
    return true;
  });

  // Speaking-share panel: spoken time when we have it, else turns.
  const totalSpokenSecs = speakerStats.reduce((s, x) => s + x.spokenSecs, 0);
  const totalTurns = speakerStats.reduce((s, x) => s + x.count, 0);
  const shareByTime = totalSpokenSecs > 0;
  const shareTotal = shareByTime ? totalSpokenSecs : totalTurns;
  const rankedStats = [...speakerStats].sort((a, b) =>
    shareByTime ? b.spokenSecs - a.spokenSecs : b.count - a.count,
  );
  const shareMax =
    rankedStats.reduce((m, s) => Math.max(m, shareByTime ? s.spokenSecs : s.count), 0) || 1;

  // Bound panel height: top N by share; keep the active filter row visible
  // when it falls outside the collapsed window (16-07-2026).
  const SHARE_BAR_CAP = 6;
  const visibleRankedStats = (() => {
    if (shareExpanded || rankedStats.length <= SHARE_BAR_CAP) {
      return rankedStats;
    }
    const top = rankedStats.slice(0, SHARE_BAR_CAP);
    if (selectedSpeakerId && !top.some((s) => s.speakerId === selectedSpeakerId)) {
      const selected = rankedStats.find((s) => s.speakerId === selectedSpeakerId);
      if (selected) return [...top, selected];
    }
    return top;
  })();

  return (
    // Room on the right for the fast-scroll handle, so it never sits on the
    // text (DES 05-10-2026: "touching the lines").
    <div className={cn(filtered.length >= TRANSCRIPT_SCRUBBER_MIN_TURNS && "pr-7")}>
      {/* One voice only: no strip, but it can still carry the wrong name. */}
      {speakerStats.length === 1 && swap && (swap.canSwap || swap.swaps.length > 0) ? (
        <div className="pt-3">
          <SpeakerSwapControl
            meetingId={swap.meetingId}
            speaker={swap.canSwap ? speakerStats[0] : null}
            otherSpeakers={speakerStats}
            swaps={swap.swaps}
            undoId={swap.canSwap ? swap.undoId : null}
          />
        </div>
      ) : null}

      {/* Flush speaking-share bands — full width of the content cell (29-07-2026).
          Chips scroll in one row; bars: top 6 + expand (16-07-2026). */}
      {speakerStats.length > 1 ? (
        <>
          <SpeakerStrip
            speakers={speakerStats}
            selectedSpeakerId={selectedSpeakerId}
            onSelect={setSelectedSpeakerId}
          />
          {swap && (swap.canSwap || swap.swaps.length > 0) ? (
            <SpeakerSwapControl
              meetingId={swap.meetingId}
              speaker={
                swap.canSwap
                  ? (speakerStats.find((s) => s.speakerId === selectedSpeakerId) ?? null)
                  : null
              }
              otherSpeakers={speakerStats}
              swaps={swap.swaps}
              undoId={swap.canSwap ? swap.undoId : null}
            />
          ) : null}

          <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3 md:px-5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {shareByTime ? "Speaking time by person" : "Turns by person"}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Info className="h-3 w-3" />
              {shareByTime ? "Estimated from segment gaps" : "By turn count"}
            </span>
          </div>

          <div className="space-y-2 border-b border-border/60 px-4 py-4 md:px-5">
            {visibleRankedStats.map((sp) => {
              const val = shareByTime ? sp.spokenSecs : sp.count;
              const frac = val / shareMax;
              const pct = shareTotal > 0 ? Math.round((val / shareTotal) * 100) : 0;
              const active = selectedSpeakerId === sp.speakerId;
              const dim = selectedSpeakerId != null && !active;
              const valueLabel = shareByTime ? fmtSpoken(sp.spokenSecs) || "0s" : `${sp.count}`;
              return (
                <div
                  key={sp.speakerId}
                  className={cn("flex items-center gap-3 transition-opacity", dim && "opacity-40")}
                >
                  <span className="w-20 shrink-0 truncate text-xs font-medium text-foreground">
                    {firstNameOf(sp.name)}
                  </span>
                  <div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-foreground/10">
                    <div
                      className={cn(
                        "h-full rounded-full",
                        active ? "bg-brand" : "bg-foreground/40",
                      )}
                      style={{ width: `${Math.max(frac * 100, 3)}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right text-xs font-semibold tabular-nums text-foreground">
                    {valueLabel}
                    <span className="ml-1 font-medium text-muted-foreground">· {pct}%</span>
                  </span>
                </div>
              );
            })}
            {rankedStats.length > SHARE_BAR_CAP ? (
              <button
                type="button"
                onClick={() => setShareExpanded((v) => !v)}
                className="pt-0.5 text-left text-[11px] font-semibold text-brand hover:underline"
              >
                {shareExpanded ? "Show less" : `Show all ${rankedStats.length}`}
              </button>
            ) : null}
            {shareByTime ? (
              <p className="pt-1 text-[11px] leading-4 text-muted-foreground">
                Spoken time is estimated from segment timings. Silence between turns counts toward
                the previous speaker, so treat it as an approximate share, not exact speech
                duration.
              </p>
            ) : null}
          </div>
        </>
      ) : null}

      <div className="relative border-b border-border/60 px-4 py-3 md:px-5">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground md:left-5" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search within transcript…"
          className="rounded-none border-0 bg-transparent px-0 pl-9 shadow-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>

      <div className="p-4 md:p-6">
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No matches for “{q}”</p>
        ) : (
          <div
            className="relative"
            onMouseUp={editable ? readSelection : undefined}
            onKeyUp={editable ? readSelection : undefined}
          >
            {picked && onEdit ? (
              <div
                role="toolbar"
                aria-label="Edit selection"
                // Keep the selection alive while the button is pressed.
                onMouseDown={(e) => e.preventDefault()}
                className="fixed z-50 flex -translate-x-1/2 -translate-y-full items-center gap-1 rounded-full border border-border bg-card p-1 shadow-lg"
                style={{ left: picked.x, top: picked.y - 8 }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const { x: _x, y: _y, ...sel } = picked;
                    setPicked(null);
                    onEdit("correct", sel);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                >
                  <PenLine className="h-3.5 w-3.5" />
                  Correct
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const { x: _x, y: _y, ...sel } = picked;
                    setPicked(null);
                    onEdit("speaker", sel);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                >
                  <UserRoundPen className="h-3.5 w-3.5" />
                  Who said this?
                </button>
              </div>
            ) : null}
            {/* Outline chat bubbles, the mobile transcript look (DES
                27-09-2026, ported 30-09-2026): speaker A left in brand,
                everyone else right in grey. */}
            <TranscriptScrubber
              listRef={turnListRef}
              turns={filtered.map((r) => ({
                speaker:
                  r.speaker?.trim() ||
                  (r.speakerId ? speakers.get(r.speakerId)?.name : undefined) ||
                  "Speaker",
                time: r.timestamp ?? null,
              }))}
            />
            <ol ref={turnListRef} className="m-0 flex list-none flex-col gap-2 p-0">
              {filtered.map((r, i) => {
                const id = r.speakerId ?? "";
                const meta = id ? speakers.get(id) : undefined;
                const name = r.speaker?.trim() || meta?.name || id || "Speaker";
                const isExternal = r.isExternal ?? meta?.isExternal ?? false;
                const displayName =
                  isExternal && !/\(client\)/i.test(name) ? `${name} (Client)` : name;
                // Same rule as the speaker chips above: a photo shows whenever
                // one exists. The stored transcript rows never carry
                // profileImageUrl (diarization writes only id/speakerId/text/
                // timestamp/startSecs, and enrichBoardroomMeetingAvatars stamps
                // participants + resolved_speakers, not transcript rows), so the
                // speaker-meta fallback is the ONLY source here — gating it on
                // isExternal left every external speaker on initials. (03-08-2026)
                const avatar = r.profileImageUrl || meta?.profileImageUrl || null;
                const isA = isAById.get(id) ?? i % 2 === 0;
                const rtl = isRTLText(r.text);

                return (
                  <TranscriptBubbleTurn
                    key={`${id}-${i}-${r.timestamp ?? ""}`}
                    isA={isA}
                    name={displayName}
                    avatarUrl={avatar}
                    initials={getInitials(name, null, null)}
                    meta={r.timestamp}
                  >
                    <p
                      dir={rtl ? "rtl" : "ltr"}
                      data-segment-id={editable && r.id ? r.id : undefined}
                      data-speaker-id={editable ? id : undefined}
                      className={cn(
                        "whitespace-pre-line break-words text-[15px] leading-relaxed text-foreground",
                        editable && "selection:bg-brand-soft",
                      )}
                    >
                      {highlightMatches(r.text ?? "", needle)}
                    </p>
                  </TranscriptBubbleTurn>
                );
              })}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Tab: Action Items (todos)
// ----------------------------------------------------------------------
function riskVariant(level?: string | null): string {
  switch (level) {
    case "high":
      return "bg-destructive/10 text-destructive ring-destructive/25";
    case "medium":
      return "bg-warning-soft text-warning ring-warning/25";
    case "low":
      return "bg-success-soft text-success ring-success/25";
    default:
      return "bg-muted text-muted-foreground ring-border";
  }
}

// Accent (icon + heading) colour for the risk popover, per level.
function riskAccentText(level?: string | null): string {
  switch (level) {
    case "high":
      return "text-destructive";
    case "medium":
      return "text-warning";
    default:
      return "text-muted-foreground";
  }
}

// Accent border colour for the risk popover, per level.
function riskAccentBorder(level?: string | null): string {
  switch (level) {
    case "high":
      return "border-destructive/40";
    case "medium":
      return "border-warning/40";
    default:
      return "border-border";
  }
}

// Level glyph — the same split mobile makes on the todo risk chip
// (MeetingTodosTab): high is the circle alert, medium the triangle. Mobile
// renders no chip at all below medium, so `low` gets the neutral circle rather
// than borrowing a warning shape it hasn't earned. (02-08-2026)
function riskLevelIcon(level?: string | null): LucideIcon {
  switch (level) {
    case "high":
      return CircleAlert;
    case "medium":
      return TriangleAlert;
    default:
      return Circle;
  }
}

// ----------------------------------------------------------------------
// RiskBadge — delay-risk chip with a click-to-open popover for the reason.
// Parity with mobile (MeetingTodosTab), but as a floating popover so it never
// pushes the row layout. Self-contained: manages its own open state and closes
// on click-outside or Escape. Only rendered when a reason exists (High/Medium —
// the synthesis prompt leaves `riskAlertReason` empty for Low). (08-07-2026)
// ----------------------------------------------------------------------
function RiskBadge({
  level,
  percent,
  reason,
}: {
  level?: string | null;
  percent?: number | null;
  reason: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const label = `${level} risk${typeof percent === "number" ? ` · ${Math.round(percent)}%` : ""}`;
  const LevelIcon = riskLevelIcon(level);

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset transition-colors hover:brightness-95 focus:outline-none focus:ring-2",
          riskVariant(level),
        )}
      >
        <LevelIcon className="h-3 w-3 shrink-0" />
        {label}
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div
          role="dialog"
          className={cn(
            "absolute left-0 top-full z-30 mt-1.5 w-64 rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg",
            riskAccentBorder(level),
          )}
        >
          <div className="flex items-start gap-2">
            <LevelIcon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", riskAccentText(level))} />
            <div className="min-w-0">
              <p
                className={cn(
                  "text-[10px] font-semibold uppercase tracking-wide",
                  riskAccentText(level),
                )}
              >
                Why this is at risk
              </p>
              <p className="mt-1 text-xs leading-relaxed">{reason}</p>
            </div>
          </div>
        </div>
      ) : null}
    </span>
  );
}

// ----------------------------------------------------------------------
// Section header meta — compact stat chips + optional hint (shared by
// Action items / Key points / Decisions). (10-07-2026)
// ----------------------------------------------------------------------
function SectionMeta({
  stats,
  hint,
}: {
  stats: Array<{
    label: string;
    value: string | number;
    tone?: "default" | "brand" | "success" | "warning";
  }>;
  hint?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {stats.map((s) => (
        <span
          key={s.label}
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums tracking-wide",
            s.tone === "brand" && "bg-brand-soft text-brand",
            s.tone === "success" && "bg-success-soft text-success",
            s.tone === "warning" && "bg-warning-soft text-warning",
            (!s.tone || s.tone === "default") && "bg-muted text-muted-foreground",
          )}
        >
          <span className="text-current/80">{s.value}</span>
          <span className="font-medium opacity-80">{s.label}</span>
        </span>
      ))}
      {hint ? (
        <span className="hidden text-[10px] text-muted-foreground sm:inline">{hint}</span>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------
// Completion stamp label — "Ticked 01 Aug 04:55 · by Dawood Ellahi (owner)".
//
// Mobile (`describeCompletionStamp` in FRONTEND/.../tabs/MeetingTodosTab.tsx)
// deliberately HIDES the actor when they are the assignee, on the grounds that
// the name repeats the assignee chip above. Web shows it **always** (04-08-2026,
// product call): the two cases are not the same fact. "Ticked" alone cannot
// distinguish the assignee ticking their own task from the owner ticking it on
// their behalf, and on a panel used to check up on a team that is exactly the
// thing worth knowing. `completedByIsOwner` is stamped by the API, so the owner
// case is marked rather than inferred from the name.
//
// Reads whichever key matches the CURRENT state: an old `completedAt` left over
// from before an untick must never render as if the task were still done.
// ----------------------------------------------------------------------
function describeCompletionStamp(todo: MeetingTodo): string | null {
  const iso = todo.completed ? todo.completedAt : todo.uncompletedAt;
  if (!iso) return null;
  const when = formatDate(iso, "dd MMM HH:mm");
  if (when === "—") return null;

  const label = `${todo.completed ? "Ticked" : "Unticked"} ${when}`;

  // Full name, not a first-name split: initial-led names ("S M Ellahi") reduce
  // to a useless "S". The row truncates instead.
  const who = String(todo.completedByName || todo.completedBy || "").trim();
  // Toggled before the actor was recorded — say nothing rather than guess.
  if (!who) return label;

  return `${label} · by ${who}${todo.completedByIsOwner ? " (owner)" : ""}`;
}

// ----------------------------------------------------------------------
// Provenance of an intel item (05-08-2026)
//
// Four states have existed in the data since 02-08 and only ONE of them was
// ever shown. The synthesis service even left the note "the UI can highlight
// 'you flagged this' without over-claiming accuracy" — and the UI never did.
//
//   manual   — typed by a person; no anchor, no quote
//   tap      — the user tapped during the meeting and the model had emitted
//              NOTHING covering it, so the tap itself became the item
//   flagged  — the model found it AND the user tapped that same moment
//   null     — plain extraction, and deliberately unlabelled: putting a chip on
//              the default turns every row into noise
//
// `flagged` says the item is real and wanted. It does NOT say the timestamp is
// more accurate — the binding step is explicit about that — so the label must
// not imply precision it doesn't have.
// ----------------------------------------------------------------------
type ItemProvenance = "manual" | "tap" | "flagged" | null;

function provenanceOf(anchor: unknown, source?: string | null): ItemProvenance {
  if (source === "manual") return "manual";
  const a = (anchor ?? null) as Record<string, unknown> | null;
  if (a?.promotedFromBookmark) return "tap";
  if (a?.userFlagged) return "flagged";
  return null;
}

const PROVENANCE_LABEL: Record<Exclude<ItemProvenance, null>, string> = {
  manual: "added by hand",
  tap: "from your tap",
  flagged: "you flagged this",
};

function ProvenanceTag({ kind }: { kind: ItemProvenance }) {
  if (!kind) return null;
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        kind === "manual"
          ? "bg-muted text-muted-foreground"
          : "bg-brand-soft text-brand",
      )}
    >
      {PROVENANCE_LABEL[kind]}
    </span>
  );
}

// ----------------------------------------------------------------------
// Manual to-do provenance line
// "Mansoor Sheikh · 05 Aug 14:10", plus "· edited 05 Aug 14:22" once changed.
// The row already claims the item was typed by a person; a name with no time
// is half that fact, and an unmarked rewrite makes the whole line untrue.
// Same DB-clock stamps and formatter as the tick line above it. (05-08-2026)
// ----------------------------------------------------------------------
function describeManualStamp(todo: MeetingTodo): string {
  const parts: string[] = [];
  if (todo.createdByName) parts.push(todo.createdByName);

  const added = todo.createdAt ? formatDate(todo.createdAt, "dd MMM HH:mm") : "—";
  if (added !== "—") parts.push(added);

  const edited = todo.editedAt ? formatDate(todo.editedAt, "dd MMM HH:mm") : "—";
  if (edited !== "—") parts.push(`edited ${edited}`);

  return parts.join(" · ");
}

/**
 * Sends this meeting's to-dos to Asana. Renders nothing at all until the
 * connector is set up — an offer to push into a system nobody has connected is
 * a dead control on the most-used screen in the product.
 */
function AsanaPushButton({ meetingId }: { meetingId: string }) {
  const status = useConnectorStatus();
  const push = usePushMeetingToAsana();
  const [result, setResult] = useState<string | null>(null);

  const ready = Boolean(status.data?.asana?.connected && status.data?.asana?.projectId);
  if (!ready) return null;
  // ADR-051 (21-10-2026): connected, but the company has not allowed Asana
  // while sharing is inside-only. Said, not hidden: the to-dos used to go.
  if (status.data?.allowedByCompany === false) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {status.data.blockedMessage ||
            "Your company admin has not allowed Asana. Ask them to turn it on in Company settings."}
        </span>
        <Button variant="outline" size="sm" disabled>
          <Share2 className="mr-2 h-3.5 w-3.5" />
          Send to Asana
        </Button>
      </div>
    );
  }

  const run = async () => {
    setResult(null);
    try {
      const r = await push.mutateAsync({ meetingId });
      const bits = [
        r.pushed ? `${r.pushed} sent` : null,
        r.updated ? `${r.updated} updated` : null,
        r.skipped ? `${r.skipped} already there` : null,
        // Completion travelled the other way — worth naming, or a tick that
        // appeared "by itself" looks like a glitch.
        r.pulled ? `${r.pulled} ticked from Asana` : null,
        r.failed ? `${r.failed} failed` : null,
      ].filter(Boolean);
      setResult(bits.length ? bits.join(" · ") : "Nothing to send");
    } catch {
      setResult("Could not reach Asana");
    }
  };

  return (
    <div className="flex items-center gap-2">
      {result ? <span className="text-xs text-muted-foreground">{result}</span> : null}
      <Button variant="outline" size="sm" onClick={run} disabled={push.isPending}>
        <Share2 className="mr-2 h-3.5 w-3.5" />
        {push.isPending ? "Sending…" : "Send to Asana"}
      </Button>
    </div>
  );
}

/** "Ali", "Ali and Sara", "Ali, Sara and Omar". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function TodosTab({
  todos,
  speakers,
  meeting,
  contentRtl = false,
  readOnly = false,
  onShare,
}: {
  todos: MeetingTodo[];
  speakers: Map<string, SpeakerMeta>;
  meeting: MeetingDetail;
  contentRtl?: boolean;
  /** Super-admin page: to-dos are read, never changed (05-10-2026). */
  readOnly?: boolean;
  /** B50: Share one to-do as text (copied); absent = no Share chip. */
  onShare?: (todoId: string) => void;
}) {
  const { user } = useAuth();
  const toggleTodo = useToggleMeetingTodo();
  const deleteTodo = useDeleteMeetingTodo();
  const dismissTodo = useDismissMeetingTodo();
  const [showDismissed, setShowDismissed] = useState(false);
  const [adding, setAdding] = useState(false);
  // Which manual to-do is open for editing. Resolved from the list rather than
  // held as an object so an edit always writes against the freshest copy.
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const editingTodo = editingTodoId ? (todos.find((t) => t.id === editingTodoId) ?? null) : null;

  // `is_owner` is absent on older API builds and defaults to true there, so
  // this only turns on when the API positively says the viewer is not the
  // owner — same guard as mobile's `isSharedViewer`.
  const isSharedViewer = meeting.is_owner === false;
  // Owner-only controls (add, edit, reassign, dismiss, follow-up, Asana). The
  // super-admin payload carries no `is_owner`, so that page passes `readOnly`:
  // a dismiss from there was refused by the server ("Could not dismiss",
  // DES 05-10-2026) because only the owner may change a meeting's to-dos.
  const canManage = !isSharedViewer && !readOnly;
  // The company roster decides who is external (todoFollowUp.ts). Owner only:
  // nobody else sets a follow-up owner.
  const roster = useAssignablePeople(canManage);
  const rosterEmails = useMemo(
    () =>
      new Set(
        [user?.email, ...(roster.data ?? []).map((p) => p.email)]
          .map((e) => String(e ?? "").trim().toLowerCase())
          .filter(Boolean),
      ),
    [roster.data, user?.email],
  );
  const ownerName =
    String(user?.name ?? "").trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "You";
  // Dismiss an AI to-do (05-10-2026): owner only, and not a corporate
  // employee — in a company account the company owns the record (the API
  // answers CORPORATE_DATA_GOVERNED otherwise).
  const isCorporateEmployee =
    user?.role === "user" && Boolean(user?.company_id ?? user?.companyId);
  const canDismiss = canManage && !isCorporateEmployee;
  // F1a: a company admin may move to-dos and rename headings in a member's meeting.
  const isCompanyAdminViewer =
    (user?.role === "company_admin" || user?.role === "admin") && Boolean(user?.company_id ?? user?.companyId);
  const dismissedTodos = todos.filter((t) => isDismissedTodo(t));
  const viewerEmail = String(user?.email ?? "")
    .trim()
    .toLowerCase();
  // Owner ticks everything; anyone else ticks only what is assigned to them,
  // matched on `assigneeEmail` — the same key the server decides on, so the UI
  // never offers a tap the API will refuse. (mirrors `canToggleTodo`)
  const canToggleTodo = useCallback(
    (todo: MeetingTodo) => {
      if (!isSharedViewer) return true;
      const assignee = String(todo.assigneeEmail ?? "")
        .trim()
        .toLowerCase();
      return Boolean(viewerEmail) && viewerEmail === assignee;
    },
    [isSharedViewer, viewerEmail],
  );

  // Voice-stamp source — same recording + evidence-anchor resolver the
  // Decisions and Key points tabs use. Hooks above must run even when empty.
  const recordingUrl = meeting.recording_url;
  const totalDuration = Number(meeting.total_duration_seconds ?? 0);
  const segmentStarts = buildSegmentStarts(meeting.transcript);
  const allTodos = todos;
  // Dismissed ones leave the list and every count; they wait below.
  todos = activeTodos(allTodos);
  const openCount = todos.filter((t) => !t.completed).length;
  const doneCount = todos.length - openCount;

  // One to-do's cell, drawn by the plain list and by every group (F1a).
  const renderTodoCell = (t: MeetingTodo, i: number, count: number, chip: ReactNode) => {
    // Email-first — see resolveTodoAssignee. Keying on assigneeSpeakerId
    // alone put the wrong person's name AND face on the task.
    const assignee = resolveTodoAssignee(t, speakers, meeting.participants);
    const reason = t.riskAlertReason?.trim();
    // Only High/Medium carry a reason worth surfacing (see synthesis prompt).
    const hasReason = Boolean(reason) && t.riskLevel !== "low";
    const anchor = resolveVoiceAnchor(t.timeInSecs, t.anchor, segmentStarts, totalDuration);
    // Raw anchor, not the padded playback span: `resolveVoiceAnchor`
    // keeps only the geometry, and the flags live on the stored object.
    const provenance = provenanceOf(t.anchor, t.source);
    return (
      <GridCell key={t.id ?? i} padded={false}>
        <ActionItemRow
          rail={{ first: i === 0, last: i === count - 1 }}
          task={t.task ?? "Untitled action"}
          anchor={anchor}
          index={i}
          recordingUrl={recordingUrl}
          assignee={assignee}
          dueDate={t.dueDate}
          completed={Boolean(t.completed)}
          riskLevel={t.riskLevel}
          delayRiskPercent={t.delayRiskPercent}
          riskReason={hasReason ? (reason as string) : null}
          rtl={contentRtl || isRTLText(t.task)}
          canToggle={Boolean(t.id) && canToggleTodo(t)}
          toggling={toggleTodo.isPending && toggleTodo.variables?.todoId === t.id}
          onToggle={() => {
            if (!t.id || !meeting.id || !canToggleTodo(t)) return;
            toggleTodo.mutate({
              meetingId: meeting.id,
              todoId: t.id,
              completed: !t.completed,
            });
          }}
          completionStamp={describeCompletionStamp(t)}
          onReassign={
            provenance !== "manual" && canManage && t.id
              ? () => setEditingTodoId(t.id ?? null)
              : undefined
          }
          onDismiss={
            provenance !== "manual" && canDismiss && t.id && meeting.id
              ? () =>
                  dismissTodo.mutate(
                    { meetingId: meeting.id as string, todoId: t.id as string, dismissed: true },
                    {
                      onSuccess: () =>
                        toast({
                          title: "To-do dismissed",
                          description: "It is kept under Dismissed, where you can restore it.",
                        }),
                      onError: (err) =>
                        toast({
                          title: "Could not dismiss this to-do",
                          description: err.message || undefined,
                          variant: "destructive",
                        }),
                    },
                  )
              : undefined
          }
          onShare={onShare && t.id ? () => onShare(t.id as string) : undefined}
          // F1a: the to-do's category; opens Move to category / topic.
          groupingChip={chip}
          rawAnchor={t.anchor}
          transcript={meeting.transcript}
          evidenceKey={`${meeting.id}:todo:${t.id ?? i}`}
          manualBy={provenance === "manual" ? (t.createdByName ?? null) : undefined}
          followUp={
            canManage && t.id && meeting.id && roster.data &&
            isExternalAssignee(t, rosterEmails) ? (
              <FollowUpMenu
                meetingId={meeting.id}
                todo={t}
                ownerName={ownerName}
                // The owner is the default row; not listed twice.
                people={roster.data.filter((p) => p.id !== user?.id)}
              />
            ) : null
          }
        />
        {/* Provenance, stated. A hand-typed item has no quote and no
          moment to play, so without a label it just looks like an
          extracted to-do the anchoring failed on — which is exactly the
          confusion the verified/unverified work exists to prevent.
          Removal is offered here and nowhere else: an extracted to-do
          is a record of what was said, and deleting it would quietly
          edit the meeting's history. (05-08-2026) */}
        {provenance ? (
          <div className="flex flex-wrap items-center gap-2 px-4 pb-2 text-[11px] text-muted-foreground md:px-5">
            <ProvenanceTag kind={provenance} />
            {/* Who AND when, and "edited" once it has been changed — the
              row claims a person typed this, and that claim goes stale
              the moment someone rewrites it. Only a hand-typed item has
              an author; a flagged or promoted one came from the
              recording, so there is nobody to name. */}
            {provenance === "manual" ? <span>{describeManualStamp(t)}</span> : null}
            {provenance === "manual" && canManage && t.id ? (
              <span className="ml-auto flex items-center gap-1">
                {/* Icons, not words: the line already carries a name and
                  two timestamps. Edit and Remove are offered on manual
                  items and nowhere else — an extracted to-do is a
                  record of what was said, so rewriting or deleting one
                  would edit the meeting's history and leave its
                  evidence anchor pointing at a sentence that no longer
                  matches. (05-08-2026) */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Edit this to-do"
                  aria-label="Edit this to-do"
                  onClick={() => setEditingTodoId(t.id ?? null)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  title="Remove this to-do"
                  aria-label="Remove this to-do"
                  disabled={deleteTodo.isPending}
                  onClick={() => {
                    if (!meeting.id || !t.id) return;
                    deleteTodo.mutate({
                      meetingId: meeting.id,
                      todoId: t.id,
                    });
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </span>
            ) : null}
          </div>
        ) : null}
        {/* Reassign, for a to-do from the recording (28-09-2026): who
          it is for can change, the wording cannot. The stamp says
          who moved it, so the row does not read as if the recording
          had named the new person. */}
        {provenance !== "manual" && (t.reassignedByName || (t.edited && t.originalTask)) && t.id ? (
          <div className="flex flex-wrap items-center gap-2 px-4 pb-2 text-[11px] text-muted-foreground md:px-5">
            {t.reassignedByName ? (
              <span className="flex items-center gap-1">
                <UserPen className="h-3 w-3" aria-hidden />
                Reassigned by {t.reassignedByName}
                {t.reassignedAt ? ` · ${formatDate(t.reassignedAt, "dd MMM HH:mm")}` : ""}
              </span>
            ) : null}
            {/* B48: the wording was changed after the meeting; what was said
                stays under it, so it still matches the evidence quote. */}
            {t.edited && t.originalTask ? (
              <span className="flex min-w-0 items-center gap-1">
                <PenLine className="h-3 w-3 shrink-0" aria-hidden />
                <span className="font-medium">Edited</span>
                {t.editedByName ? ` by ${t.editedByName}` : ""}
                <span className="min-w-0 truncate" dir={isRTLText(t.originalTask) ? "rtl" : "ltr"}>
                  · Said: “{t.originalTask}”
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
        {/* To-do-only access (DES 12-10-2026): people given this
          to-do who cannot see the meeting. Owner only; "Share
          meeting" opens the usual share menu, its search prefilled
          with the first name. */}
        {canManage && t.id && meeting.id && meeting.todo_only_access?.[t.id]?.length ? (
          <div className="flex flex-wrap items-center gap-2 px-4 pb-2 text-[11px] text-muted-foreground md:px-5">
            <span className="flex items-center gap-1">
              <Lock className="h-3 w-3" aria-hidden />
              {joinNames(meeting.todo_only_access[t.id])} can see this to-do only
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="font-semibold text-brand hover:underline"
                >
                  Share meeting
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1">
                <MeetingShareMenu
                  meetingId={meeting.id}
                  initialQuery={meeting.todo_only_access[t.id][0]}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </GridCell>
    );
  };

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <TodoTick className="h-4 w-4 text-brand" />
          Action items
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <SectionMeta
            stats={[
              {
                value: openCount,
                label: "open",
                tone: openCount > 0 ? "brand" : "default",
              },
              {
                value: doneCount,
                label: "done",
                tone: doneCount > 0 ? "success" : "default",
              },
              { value: todos.length, label: "total" },
            ]}
            hint={todos.length > 0 ? "· click a row to see where it was said" : undefined}
          />
          {/* Explicit, per meeting. Pushing automatically on synthesis would
              fill someone's Asana with tasks they never asked for and cannot
              undo — so the decision stays a human one. Owner only: a shared
              viewer sending another company's to-dos into their own tracker is
              an exfiltration route, not a convenience. (05-08-2026) */}
          {canManage ? (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
              <Plus className="mr-2 h-3.5 w-3.5" /> Add to-do
            </Button>
          ) : null}
          {canManage ? <AsanaPushButton meetingId={meeting.id} /> : null}
        </div>
      </GridBand>

      {/* One sheet serves both paths — the header button opens it empty, a
          row's pencil opens it filled. It replaced an inline card that worked
          for adding (always at the top) and had nowhere sensible to put an
          edit: a form for a row near the bottom would open far from the row it
          belonged to. (05-08-2026) */}
      {canManage ? (
        <TodoFormSheet
          open={adding || editingTodo !== null}
          onOpenChange={(next: boolean) => {
            if (next) return;
            setAdding(false);
            setEditingTodoId(null);
          }}
          meetingId={meeting.id}
          editing={editingTodo}
          speakers={[...speakers.entries()].map(([speakerId, m]) => ({
            speakerId,
            name: m.name,
            email: m.email,
            avatarUrl: m.profileImageUrl,
          }))}
          externalAssignees={meeting.external_assignees}
        />
      ) : null}

      {/* Shared viewers see every task but tick only their own; the owner ticks
          all. A locked row loses the tap, not the information — its checkbox
          still shows the real state. (04-08-2026) */}
      {isSharedViewer && !readOnly ? (
        <GridBand className="flex items-center gap-2 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5 shrink-0" />
          Shared with you — you can tick the tasks assigned to you
        </GridBand>
      ) : null}

      {todos.length === 0 ? (
        <div className="p-4 md:p-6">
          <EmptyTab
            icon={TodoTick}
            title="No action items"
            hint="No concrete follow-ups were captured for this meeting."
          />
        </div>
      ) : (
        <TodoGroupsBody
          todos={todos}
          meetingId={meeting.id}
          personOf={(t) => resolveTodoAssignee(t, speakers, meeting.participants)?.name ?? null}
          // The server decides (owner, assignee, follow-up owner, company
          // admin); this only hides a chip it would refuse.
          canMove={(t) => !readOnly && (canToggleTodo(t) || isCompanyAdminViewer)}
          canEditTopics={!readOnly && (canManage || isCompanyAdminViewer)}
          renderCell={renderTodoCell}
        />
      )}

      {/* Dismissed AI to-dos (05-10-2026): kept, closed by default, each one
          restorable. Owner only — nobody else can change them. */}
      {!isSharedViewer && dismissedTodos.length > 0 ? (
        <>
          <GridBand className="p-0">
            <button
              type="button"
              onClick={() => setShowDismissed((v) => !v)}
              aria-expanded={showDismissed}
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-xs font-medium text-muted-foreground transition-colors hover:text-foreground md:px-5"
            >
              {showDismissed ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              <EyeOff className="h-3.5 w-3.5" />
              Dismissed ({dismissedTodos.length})
              <span className="font-normal">· hidden from counts, reports and Asana</span>
            </button>
          </GridBand>
          {showDismissed ? (
            <GridCells className="grid-cols-1">
              {dismissedTodos.map((t, i) => (
                <GridCell key={t.id ?? `d${i}`} padded={false}>
                  <div className="flex items-start gap-3 px-4 py-3 md:px-5">
                    <div className="min-w-0 flex-1">
                      <p
                        dir={contentRtl || isRTLText(t.task) ? "rtl" : "ltr"}
                        className="text-sm leading-snug text-muted-foreground line-through decoration-foreground/20"
                      >
                        {t.task ?? "Untitled action"}
                      </p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Dismissed
                        {t.dismissedByName ? ` by ${t.dismissedByName}` : ""}
                        {t.dismissedAt ? ` · ${formatDate(t.dismissedAt, "dd MMM HH:mm")}` : ""}
                      </p>
                    </div>
                    {canDismiss && t.id && meeting.id ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 shrink-0 rounded-full px-3 text-xs"
                        disabled={dismissTodo.isPending}
                        onClick={() =>
                          dismissTodo.mutate(
                            { meetingId: meeting.id as string, todoId: t.id as string, dismissed: false },
                            {
                              onError: () =>
                                toast({ title: "Could not restore this to-do", variant: "destructive" }),
                            },
                          )
                        }
                      >
                        Restore
                      </Button>
                    ) : null}
                  </div>
                </GridCell>
              ))}
            </GridCells>
          ) : null}
        </>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------
// Follow-up owner (05-10-2026, DES). A to-do for someone outside the company
// (Karen at a client) is done by them and chased by someone here: the meeting
// owner by default, or anyone in the company picked from this menu. Asana gets
// the task under that person, titled "Follow up with Karen: ...".
// Related: BACKEND boardroom/todoFollowUp.mjs
// ----------------------------------------------------------------------
const FOLLOW_UP_OWNER = "__owner__";

function FollowUpMenu({
  meetingId,
  todo,
  ownerName,
  people,
}: {
  meetingId: string;
  todo: MeetingTodo;
  ownerName: string;
  people: { id: string; name?: string; email?: string; avatarUrl?: string | null }[];
}) {
  const [open, setOpen] = useState(false);
  const setFollowUp = useSetTodoFollowUp();
  const who = String(todo.assigneeName ?? todo.assigneeEmail ?? "").trim() || "them";
  const current = todo.followUpUserId
    ? String(todo.followUpName ?? todo.followUpEmail ?? "").trim() || "Someone"
    : ownerName;

  const items: SearchableMenuItem[] = useMemo(
    () => [
      {
        value: FOLLOW_UP_OWNER,
        label: `${ownerName} (meeting owner)`,
        description: "Default",
        icon: (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <UserCheck className="h-3.5 w-3.5" aria-hidden />
          </span>
        ),
      },
      ...people.map((p) => ({
        value: p.id,
        label: p.name || p.email || p.id,
        description: p.name && p.email ? p.email : undefined,
        keywords: p.email,
        icon: (
          <Avatar src={p.avatarUrl ?? null} initials={getInitials(p.name, null, p.email)} alt="" size="sm" />
        ),
      })),
    ],
    [people, ownerName],
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={setFollowUp.isPending}
          title={`${who} is outside your company. ${current} follows it up, and gets it in Asana.`}
          aria-label="Change who follows this up"
          className="inline-flex h-[22px] items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] px-2 text-[11px] text-muted-foreground transition-colors hover:bg-black/[0.08] dark:hover:bg-white/[0.1] hover:text-foreground disabled:opacity-60"
        >
          {setFollowUp.isPending ? (
            <LoaderCircle className="h-3 w-3 animate-spin" />
          ) : (
            <UserCheck className="h-3 w-3" />
          )}
          Follow-up: <span className="font-medium text-foreground">{current}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-1.5">
        <SearchableMenuList
          title="Who follows this up?"
          searchPlaceholder="Search people"
          items={items}
          noMatchText="No people match that search."
          selected={[todo.followUpUserId ?? FOLLOW_UP_OWNER]}
          onSelect={(id) => {
            setOpen(false);
            const userId = id === FOLLOW_UP_OWNER ? null : id;
            if (userId === (todo.followUpUserId ?? null) || !todo.id) return;
            setFollowUp.mutate(
              { meetingId, todoId: todo.id, userId },
              {
                onError: () =>
                  toast({ title: "Could not change who follows up", variant: "destructive" }),
              },
            );
          }}
        />
        <p className="px-2 pb-1 pt-1.5 text-[11px] leading-4 text-muted-foreground">
          {who} is not in your company. The person you pick chases it, and gets it in Asana.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ----------------------------------------------------------------------
// Action item row — flush GridCell body (04-08-2026), same play-row
// language as Key points / Decisions, plus assignee / due / risk chips.
// ----------------------------------------------------------------------
function ActionItemRow({
  task,
  anchor,
  index,
  recordingUrl,
  assignee,
  dueDate,
  completed,
  riskLevel,
  delayRiskPercent,
  riskReason,
  rtl = false,
  rail,
  canToggle,
  toggling,
  onToggle,
  completionStamp,
  onReassign,
  onDismiss,
  onShare,
  rawAnchor,
  transcript,
  manualBy,
  followUp,
  groupingChip,
  evidenceKey,
}: {
  task: string;
  anchor: VoiceAnchor | null;
  index: number;
  recordingUrl: string | null | undefined;
  assignee: SpeakerMeta;
  dueDate?: string | null;
  completed: boolean;
  riskLevel?: string | null;
  delayRiskPercent?: number | null;
  riskReason: string | null;
  rtl?: boolean;
  /** Place in the to-do timeline (the phone's rail through the checkboxes). */
  rail?: { first: boolean; last: boolean };
  canToggle: boolean;
  toggling: boolean;
  onToggle: () => void;
  /** "Ticked 01 Aug 04:55 · by X", or null until the task has been toggled. */
  completionStamp: string | null;
  /** Owner, AI to-do: the Reassign pill, first in the meta row before the
   *  assignee (the phone's order, DES 30-09-2026). */
  onReassign?: () => void;
  /** Owner, AI to-do: hide it (kept, restorable). 05-10-2026. */
  onDismiss?: () => void;
  /** B50: copy this one to-do as text (anyone who can export). */
  onShare?: () => void;
  /** Where it was said, opened under the row on click (05-10-2026). */
  rawAnchor?: unknown;
  transcript?: MeetingDetail["transcript"];
  /** Set (possibly empty) for a hand-added to-do: it has no moment. */
  manualBy?: string | null;
  /** Unique on the page: one evidence panel open at a time. */
  evidenceKey: string;
  /** Owner, external assignee: who here chases it (05-10-2026). */
  followUp?: ReactNode;
  /** F1a: the category chip (move to category / topic). */
  groupingChip?: ReactNode;
}) {
  // A click opens where it was said under the row; no play chip to hit by
  // accident (DES 05-10-2026, the phone's press-and-hold sheet in place).
  const [open, toggle, close] = useEvidenceOpen(evidenceKey);
  const isManual = manualBy !== undefined;
  const label = anchor && !isManual ? anchorLabel(anchor) : String(index + 1).padStart(2, "0");

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      title={open ? undefined : "Show where this was said"}
      className={cn(
        "group relative grid cursor-pointer grid-cols-[1.5rem_3.5rem_minmax(0,1fr)] items-start gap-x-2.5 rounded-xl px-4 py-3 [--rail-bottom:36px] [--rail-top:14px] [--rail-x:27px] sm:[--rail-bottom:38px] sm:[--rail-top:16px] sm:[--rail-x:31px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:grid-cols-[1.5rem_4rem_minmax(0,1fr)] sm:gap-x-3 sm:px-5 sm:py-3.5",
        // No hover tint while open: the evidence panel has its own fill.
        !open && "hover:bg-black/[0.02] dark:hover:bg-white/[0.03]",
        completed && "opacity-70",
      )}
      dir={rtl ? "rtl" : "ltr"}
    >
      {/* Checkbox: 22px at the 16px (sm 20px) padding, 2px below the 12px
          (sm 14px) padding. The row is `dir`-aware, so start = the icon side. */}
      {rail ? (
        <TimelineRail
          first={rail.first}
          last={rail.last}
          rtl={rtl}
          x="var(--rail-x)"
          top="var(--rail-top)"
          bottom="var(--rail-bottom)"
        />
      ) : null}
      {/* The row's own click plays the clip, so the checkbox must stop the
            event — otherwise ticking a task also starts audio. A locked row
            keeps a REAL checkbox rather than a lock glyph: a viewer who cannot
            tick must still be able to read whether the task is done. Only the
            box dims; the text stays at full contrast. (04-08-2026, mirrors
            MeetingTodosTab.tsx) */}
      <div
        className="pt-0.5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          role="checkbox"
          aria-checked={completed}
          aria-label={completed ? "Mark as not done" : "Mark as done"}
          disabled={!canToggle || toggling}
          onClick={onToggle}
          title={
            canToggle
              ? completed
                ? "Mark as not done"
                : "Mark as done"
              : "Only the meeting owner or this task's assignee can update it"
          }
          className={cn(
            "flex h-[22px] w-[22px] items-center justify-center rounded-full transition-colors",
            // Mobile's tick is `VZPickerCheckIcon`: a stroke CircleCheck in
            // brand when done, a plain Circle outline when open — NOT a
            // filled box. Same glyph, same 22px, same brand red (#E52421 ≙
            // --brand) so the two clients read as one product. (04-08-2026)
            completed ? "text-brand" : "text-muted-foreground/50",
            canToggle
              ? "hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              : "cursor-not-allowed opacity-45",
            toggling && "opacity-60",
          )}
        >
          {toggling ? (
            <LoaderCircle className="h-[22px] w-[22px] animate-spin" />
          ) : completed ? (
            <CircleCheck className="h-[22px] w-[22px]" />
          ) : (
            <Circle className="h-[22px] w-[22px]" />
          )}
        </button>
      </div>

      <div className="min-w-0">
        <p
          className={cn(
            "font-mono text-sm font-semibold tabular-nums tracking-tight sm:text-[15px]",
            "text-foreground",
          )}
        >
          {label}
        </p>
        <p className="mt-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground sm:text-[10px]">
          {completed ? "Done" : `Action ${index + 1}`}
        </p>
      </div>

      <div className="min-w-0 pt-0.5">
        <p
          dir={rtl ? "rtl" : "ltr"}
          className={cn(
            // ×24/22 for Latin (DES 08-10-2026, the phone's 22 → 24):
            // snug 1.375 → 1.5, relaxed 1.625 → 1.77.
            "text-sm text-foreground/90",
            rtl ? "leading-snug sm:leading-relaxed" : "leading-normal sm:leading-[1.77]",
            completed && "line-through decoration-foreground/30",
          )}
        >
          {task}
        </p>
        <div
          className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Assignee shows a FACE, not a generic person glyph — parity with
                the mobile ToDos rows (AssigneeAvatar). A photo renders whenever
                one exists; `isExternal` only tints the initials fallback, never
                suppresses the photo. (03-08-2026) */}
          {onReassign ? (
            <button
              type="button"
              onClick={onReassign}
              title="Fix the wording, the due date or who it is for"
              aria-label="Edit this to-do"
              className="inline-flex h-[22px] items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-black/[0.08] dark:hover:bg-white/[0.1]"
            >
              {/* B48: "Edit to-do" (was Reassign, DES 02-11-2026). */}
              <UserPen className="h-3 w-3" />
              Edit
            </button>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              title="Not a real task? Hide it. It stays in the meeting and can be restored."
              aria-label="Dismiss this to-do"
              className="inline-flex h-[22px] items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-black/[0.08] dark:hover:bg-white/[0.1]"
            >
              <EyeOff className="h-3 w-3" />
              Dismiss
            </button>
          ) : null}
          {onShare ? (
            <button
              type="button"
              onClick={onShare}
              title="Copy this to-do as text, to paste into an email or a chat"
              aria-label="Share this to-do as text"
              className="inline-flex h-[22px] items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-black/[0.08] dark:hover:bg-white/[0.1]"
            >
              <Share2 className="h-3 w-3" />
              Share
            </button>
          ) : null}
          <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] py-0.5 pl-0.5 pr-2 text-[11px] text-foreground/80">
            <Avatar
              src={assignee.profileImageUrl}
              initials={getInitials(assignee.name, null, assignee.email)}
              alt=""
              size="xs"
              tone={assignee.isExternal ? "neutral" : "brand"}
            />
            {assignee.name}
          </span>
          {followUp}
          {dueDate ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] px-1.5 py-0.5 text-[11px] text-foreground/80">
              <CalendarClock className="h-3 w-3" />
              {dueDate}
            </span>
          ) : null}
          {riskLevel ? (
            riskReason ? (
              <RiskBadge level={riskLevel} percent={delayRiskPercent} reason={riskReason} />
            ) : (
              (() => {
                const LevelIcon = riskLevelIcon(riskLevel);
                return (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset",
                      riskVariant(riskLevel),
                    )}
                  >
                    <LevelIcon className="h-3 w-3 shrink-0" />
                    {riskLevel} risk
                    {typeof delayRiskPercent === "number"
                      ? ` · ${Math.round(delayRiskPercent)}%`
                      : ""}
                  </span>
                );
              })()
            )
          ) : null}
          {groupingChip}
        </div>

        {/* When it was last ticked / unticked. Renders only once the task has
              actually been toggled — never a placeholder. (04-08-2026) */}
        {completionStamp ? (
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {completed ? (
              <CircleCheck className="h-3 w-3 shrink-0" />
            ) : (
              <Circle className="h-3 w-3 shrink-0" />
            )}
            <span className="truncate">{completionStamp}</span>
          </p>
        ) : null}

        <EvidenceCollapse open={open}>
          <MeetingEvidenceInline
            anchor={anchor}
            rawAnchor={rawAnchor}
            transcript={transcript}
            recordingUrl={recordingUrl}
            empty={isManual ? "manual" : null}
            manualBy={manualBy ?? null}
            onClose={close}
          />
        </EvidenceCollapse>
      </div>

    </div>
  );
}

// ----------------------------------------------------------------------
// The evidence-anchored playback resolver (`resolveVoiceAnchor` and friends)
// and `splitRiskLines` moved to meetingEvidence.ts (04-08-2026) — the Mindmap
// tab needs the same resolver, and a second copy of *that* logic is the exact
// bug the anchor work fixed. Imported at the top of this file.
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Shared stamp-moment row — used by Key points + Decisions.
// Editorial list: large clock, body copy, play control; whole row plays.
// (10-07-2026)
// ----------------------------------------------------------------------

function StampMomentRow({
  point,
  anchor,
  index,
  recordingUrl,
  kind = "Moment",
  rtl = false,
  provenance = null,
}: {
  point: string;
  anchor: VoiceAnchor | null;
  index: number;
  recordingUrl: string | null | undefined;
  kind?: string;
  rtl?: boolean;
  provenance?: ItemProvenance;
}) {
  const playRef = useRef<HTMLButtonElement>(null);
  const [playing, setPlaying] = useState(false);
  const canPlay = Boolean(recordingUrl && anchor);
  // Ordinal, not a fabricated clock, when the moment could not be located.
  const label = anchor ? anchorLabel(anchor) : String(index + 1).padStart(2, "0");

  const triggerPlay = () => {
    if (!canPlay) return;
    playRef.current?.click();
  };

  return (
    <div
      role={canPlay ? "button" : undefined}
      tabIndex={canPlay ? 0 : undefined}
      onClick={triggerPlay}
      onKeyDown={(e) => {
        if (!canPlay) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          triggerPlay();
        }
      }}
      title={canPlay ? (playing ? "Pause clip" : `Play from ${label}`) : undefined}
      className={cn(
        "group grid grid-cols-[3.5rem_minmax(0,1fr)_2rem] items-start gap-x-2.5 px-4 py-3 transition-colors sm:grid-cols-[4rem_minmax(0,1fr)_2.25rem] sm:gap-x-3 sm:px-5 sm:py-3.5",
        canPlay &&
          "cursor-pointer hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        playing && "bg-brand-soft/45",
      )}
      dir={rtl ? "rtl" : "ltr"}
    >
      <div className="min-w-0">
        <p
          className={cn(
            "font-mono text-sm font-semibold tabular-nums tracking-tight sm:text-[15px]",
            playing ? "text-brand" : "text-foreground",
          )}
        >
          {label}
        </p>
        <p className="mt-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground sm:text-[10px]">
          {playing ? "Playing" : `${kind} ${index + 1}`}
        </p>
      </div>

      <div className="min-w-0 pt-0.5">
        <p
          dir={rtl ? "rtl" : "ltr"}
          className="text-sm leading-snug text-foreground/90 sm:leading-relaxed"
        >
          {point}
        </p>
        {provenance ? (
          <div className="mt-1.5">
            <ProvenanceTag kind={provenance} />
          </div>
        ) : null}
      </div>

      <div
        className="pt-0.5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {canPlay && anchor ? (
          <AudioPlayBadge
            ref={playRef}
            src={recordingUrl}
            variant="original"
            startSecs={Math.max(0, anchor.startSecs)}
            clipSecs={anchorClipSecs(anchor)}
            // Same speaker glyph the mobile voice stamp uses — see TodoRow.
            icon={<Volume1 className="h-3.5 w-3.5" />}
            title={`Play clip at ${label}`}
            onPlayingChange={setPlaying}
            className="h-8 w-8 justify-center border-transparent bg-muted p-0 text-foreground/55 opacity-70 transition-all hover:bg-brand-soft hover:text-brand group-hover:opacity-100"
            playingClassName="border-brand bg-brand text-brand-foreground opacity-100 hover:bg-brand hover:text-brand-foreground"
          />
        ) : (
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full bg-muted"
            aria-hidden
          >
            <span className="h-1.5 w-1.5 rounded-full bg-foreground/30" />
          </span>
        )}
      </div>
    </div>
  );
}

// One key point, the phone's look (DES 14-10-2026): the bulb on a line that
// runs through the topic's bulbs (stops at the first and last), the point,
// and the clip button. A click on the row plays where it was said.
function KeyPointTimelineRow({
  point,
  anchor,
  recordingUrl,
  isFirst,
  isLast,
  rtl = false,
  provenance = null,
}: {
  point: string;
  anchor: VoiceAnchor | null;
  recordingUrl: string | null | undefined;
  isFirst: boolean;
  isLast: boolean;
  rtl?: boolean;
  provenance?: ItemProvenance;
}) {
  const playRef = useRef<HTMLButtonElement>(null);
  const [playing, setPlaying] = useState(false);
  const canPlay = Boolean(recordingUrl && anchor);
  const label = anchor ? anchorLabel(anchor) : "";
  const triggerPlay = () => {
    if (canPlay) playRef.current?.click();
  };
  return (
    <div
      role={canPlay ? "button" : undefined}
      tabIndex={canPlay ? 0 : undefined}
      onClick={triggerPlay}
      onKeyDown={(e) => {
        if (!canPlay) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          triggerPlay();
        }
      }}
      title={canPlay ? (playing ? "Pause clip" : `Play from ${label}`) : undefined}
      className={cn(
        "group flex items-start gap-3 rounded-xl transition-colors",
        canPlay && "cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        playing && "bg-brand-soft/40",
      )}
      dir={rtl ? "rtl" : "ltr"}
    >
      {/* Rail: the line runs the row's full height except past the first and
          last bulb; the bulb sits on a page-coloured disc so the line stops
          short of it. Bulb centred on the first 24px line. */}
      <div className="relative w-[22px] shrink-0 self-stretch" aria-hidden>
        {!(isFirst && isLast) ? (
          <span
            className={cn(
              "absolute left-1/2 w-0.5 -translate-x-1/2 rounded-full bg-app-text/15",
              isFirst ? "top-[17px]" : "top-0",
              isLast ? "h-[17px]" : "bottom-0",
            )}
          />
        ) : null}
        <span className="relative mt-[6px] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-background">
          <Lightbulb className="h-[18px] w-[18px] text-app-text" strokeWidth={1.75} />
        </span>
      </div>
      <div className="min-w-0 flex-1 py-[5px]">
        <p dir={rtl ? "rtl" : "ltr"} className="text-base leading-6 text-app-text">
          {point}
        </p>
        {provenance ? (
          <div className="mt-1.5">
            <ProvenanceTag kind={provenance} />
          </div>
        ) : null}
      </div>
      <div
        className="pt-1"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {canPlay && anchor ? (
          <AudioPlayBadge
            ref={playRef}
            src={recordingUrl}
            variant="original"
            startSecs={Math.max(0, anchor.startSecs)}
            clipSecs={anchorClipSecs(anchor)}
            icon={<Volume1 className="h-3.5 w-3.5" />}
            title={`Play clip at ${label}`}
            onPlayingChange={setPlaying}
            className="h-8 w-8 justify-center border-transparent bg-transparent p-0 text-foreground/40 opacity-0 transition-all hover:bg-brand-soft hover:text-brand group-hover:opacity-100 focus-visible:opacity-100"
            playingClassName="border-brand bg-brand text-brand-foreground opacity-100 hover:bg-brand hover:text-brand-foreground"
          />
        ) : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Key points tab — takeaways that are neither decisions nor todos.
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Agenda coverage — what the owner planned vs what happened (19-08-2026)
//
// Owner-only: the API omits `agenda_coverage` for every other reader, so this
// panel is only ever reached on your own meeting and never re-checks that.
//
// Mirrors mobile's Agenda tab, including the two rules that are easy to get
// wrong: a `not_raised` row carries NO stamp (there is no moment to play, and a
// dimmed one would suggest we looked and failed), and the three statuses use
// distinct glyphs rather than a colour ramp — "raised but unresolved" is a
// different outcome from "covered", not a milder one.
// Related: FRONTEND/.../MeetingDetails/tabs/MeetingCoverageTab.tsx, ADR-023
// ----------------------------------------------------------------------
const COVERAGE_META = {
  covered_decided: {
    Icon: CircleCheck,
    label: "Covered",
    // Says what the status MEANS, not just its name (DES 05-10-2026: "the
    // agenda's purpose is not understandable").
    hint: "Discussed and settled",
    cls: "text-success",
    tone: "success",
  },
  discussed_open: {
    Icon: CircleDashed,
    label: "Left open",
    hint: "Discussed, but not settled",
    cls: "text-warning",
    tone: "warning",
  },
  not_raised: {
    Icon: CircleSlash,
    label: "Not raised",
    hint: "Never came up",
    cls: "text-muted-foreground",
    tone: "default",
  },
} as const;

type CoverageStatus = keyof typeof COVERAGE_META;

// One agenda point. Built for this tab rather than borrowing StampMomentRow:
// that row's "<kind> <n>" caption rendered "COVERED 2", which read as a count
// rather than as status + position, and it had nowhere for the answer.
// Layout follows mobile: status glyph · the point · the answer · a meta line
// with the status and the time it was discussed.
function AgendaCoverageRow({
  point,
  answer,
  status,
  anchor,
  recordingUrl,
  rtl,
  rawAnchor,
  transcript,
  evidenceKey,
  tick = null,
  match = null,
}: {
  point: string;
  answer: string | null;
  /** null (B49): no AI verdict for this item (not processed, or a viewer). */
  status: CoverageStatus | null;
  anchor: VoiceAnchor | null;
  recordingUrl: string | null | undefined;
  rtl: boolean;
  rawAnchor: unknown;
  transcript: MeetingDetail["transcript"];
  evidenceKey: string;
  /** B49: the person's tick from the recorder; null = no check box. */
  tick?: { ticked: boolean; label: string | null; anchor: VoiceAnchor | null } | null;
  /** B49 `match`: how the tick and the AI compare. */
  match?: string | null;
}) {
  // A click opens where it was said under the row (05-10-2026).
  const [open, toggle, close] = useEvidenceOpen(evidenceKey);
  // B49: the tick's moment opens in its own panel (one panel at a time).
  const [tickOpen, toggleTick, closeTick] = useEvidenceOpen(`${evidenceKey}:tick`);
  const meta = status ? (COVERAGE_META[status] ?? COVERAGE_META.not_raised) : null;
  const Icon = meta ? meta.Icon : tick?.ticked ? SquareCheck : Square;
  // A not_raised point has no moment — see the block comment above.
  const shownAnchor = status === "not_raised" || !status ? null : anchor;
  // With no AI verdict (a viewer, or before processing) the row opens the tick's moment.
  const rowToggle = status ? toggle : tick?.ticked && tick.anchor ? toggleTick : () => {};
  const approx = shownAnchor?.confidence === "approx";
  const clock = shownAnchor ? formatClock(shownAnchor.markerSecs) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={status ? open : tickOpen}
      onClick={rowToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          rowToggle();
        }
      }}
      title={open || !status ? undefined : "Show where this was said"}
      className={cn(
        "group grid cursor-pointer grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 rounded-xl px-4 py-3.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-5",
        !open && "hover:bg-black/[0.02] dark:hover:bg-white/[0.03]",
      )}
      dir={rtl ? "rtl" : "ltr"}
    >
      <Icon
        className={cn(
          "mt-0.5 h-[18px] w-[18px]",
          meta ? meta.cls : tick?.ticked ? "text-foreground" : "text-muted-foreground",
        )}
        aria-hidden
      />

      <div className="min-w-0">
        <p className="text-sm leading-snug text-foreground sm:leading-relaxed">{point}</p>
        {answer ? (
          <p className="mt-1 text-sm leading-snug text-muted-foreground sm:leading-relaxed">
            {answer}
          </p>
        ) : null}
        <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-xs">
          {/* B49: the person's tick first, then the AI's verdict. When they
              disagree both stay, quietly; the AI never changes the tick. */}
          {tick ? (
            tick.ticked ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (tick.anchor) toggleTick();
                }}
                disabled={!tick.anchor}
                title={tick.anchor ? "Hear the moment it was ticked" : undefined}
                aria-expanded={tick.anchor ? tickOpen : undefined}
                className="inline-flex items-center gap-1 rounded font-medium text-foreground underline-offset-2 hover:underline disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Check className="h-3.5 w-3.5" aria-hidden />
                {tick.label ? `Ticked at ${tick.label}` : "Ticked"}
              </button>
            ) : match === "agree_open" ? null : (
              <span className="text-muted-foreground">Not ticked</span>
            )
          ) : null}
          {tick && meta && !(match === "agree_open" && !tick.ticked) ? (
            <span className="text-muted-foreground/60" aria-hidden>
              ·
            </span>
          ) : null}
          {meta ? (
            match === "ticked_not_found" ? (
              <span className="text-muted-foreground" title={meta.hint}>
                AI found no discussion
              </span>
            ) : (
              <span className={cn("font-medium", meta.cls)} title={meta.hint}>
                {meta.label}
              </span>
            )
          ) : null}
          {clock ? (
            <>
              <span className="text-muted-foreground/60" aria-hidden>
                ·
              </span>
              <span
                className="tabular-nums text-muted-foreground"
                title={approx ? "Approximate — the exact moment could not be pinned down" : undefined}
              >
                {`${approx ? "around" : "at"} ${clock}`}
              </span>
            </>
          ) : null}
        </p>
        {status ? (
          <EvidenceCollapse open={open}>
            <MeetingEvidenceInline
              anchor={shownAnchor}
              rawAnchor={rawAnchor}
              transcript={transcript}
              recordingUrl={recordingUrl}
              empty={status === "not_raised" ? "not_raised" : null}
              onClose={close}
            />
          </EvidenceCollapse>
        ) : null}
        {tick?.ticked && tick.anchor ? (
          <EvidenceCollapse open={tickOpen}>
            <MeetingEvidenceInline
              heading="When it was ticked"
              anchor={tick.anchor}
              rawAnchor={null}
              transcript={transcript}
              recordingUrl={recordingUrl}
              onClose={closeTick}
            />
          </EvidenceCollapse>
        ) : null}
      </div>

    </div>
  );
}

export function AgendaCoverageTab({
  meeting,
  contentRtl = false,
}: {
  meeting: MeetingDetail;
  contentRtl?: boolean;
}) {
  // B49: the agenda ticked during the meeting wins when the recorder sent it.
  if ((meeting.agenda_check?.items ?? []).length > 0) {
    return <AgendaCheckTab meeting={meeting} contentRtl={contentRtl} />;
  }
  const coverage = (meeting.agenda_coverage ?? [])
    .map((c) => ({
      point: c?.point ?? "",
      answer: typeof c?.answer === "string" && c.answer.trim() ? c.answer.trim() : null,
      noteId: c?.noteId ?? null,
      noteTitle: c?.noteTitle ?? null,
      status: (c?.status ?? "not_raised") as CoverageStatus,
      timeInSecs: typeof c?.timeInSecs === "number" ? c.timeInSecs : null,
      anchor: c?.anchor ?? null,
    }))
    // Order is the ORDER THE USER WROTE THEM, never sorted by time or status —
    // it is their list, and reordering it makes it someone else's.
    .filter((c) => !!c.point);

  if (coverage.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <EmptyTab
          icon={AgendaFlag} // DES 08-10-2026: app icon, was ClipboardCheck
          title="No agenda attached"
          hint="Attach a written note to a meeting before it is processed, and its points are checked against what was discussed."
        />
      </div>
    );
  }

  const count = (s: CoverageStatus) => coverage.filter((c) => c.status === s).length;
  const counts = {
    covered_decided: count("covered_decided"),
    discussed_open: count("discussed_open"),
    not_raised: count("not_raised"),
  };
  // Several agenda notes (mobile, 28-09-2026): group points under their note.
  const multiNote = new Set(coverage.map((c) => c.noteId).filter(Boolean)).size > 1;
  const recordingUrl = meeting.recording_url;
  const totalDuration = Number(meeting.total_duration_seconds ?? 0);
  const segmentStarts = buildSegmentStarts(meeting.transcript);

  return (
    <>
      <GridBand className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {/* DES 08-10-2026: app icon, was ClipboardCheck */}
            <AgendaFlag className="h-4 w-4 text-brand" />
            Agenda
          </h3>
          <SectionMeta
            stats={(Object.keys(COVERAGE_META) as CoverageStatus[])
              .filter((s) => counts[s] > 0)
              .map((s) => ({
                value: counts[s],
                label: COVERAGE_META[s].label.toLowerCase(),
                tone: COVERAGE_META[s].tone,
              }))}
          />
        </div>
        <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">
          The points from your agenda note, in the order you wrote them, checked against what was
          said. Each shows whether it was settled, left open or never raised, and where it came
          up — click a point to see where it was said.
        </p>
        {/* The share of the agenda that was settled, open and missed — read at
            a glance before the list. */}
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          <div
            className="bg-success"
            style={{ width: `${(counts.covered_decided / coverage.length) * 100}%` }}
          />
          <div
            className="bg-warning"
            style={{ width: `${(counts.discussed_open / coverage.length) * 100}%` }}
          />
        </div>
      </GridBand>

      <GridCells className="grid-cols-1">
        {coverage.map((c, i) => {
          const showHeading =
            multiNote && (i === 0 || coverage[i - 1].noteId !== c.noteId);
          return (
            <GridCell key={i} padded={false}>
              {showHeading ? (
                <p className="px-4 pt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:px-5">
                  {c.noteTitle || "Agenda note"}
                </p>
              ) : null}
              <AgendaCoverageRow
                point={c.point}
                answer={c.answer}
                status={c.status}
                anchor={resolveVoiceAnchor(c.timeInSecs, c.anchor, segmentStarts, totalDuration)}
                recordingUrl={recordingUrl}
                rtl={contentRtl || isRTLText(c.point)}
                rawAnchor={c.anchor}
                transcript={meeting.transcript}
                evidenceKey={`${meeting.id}:agenda:${i}`}
              />
            </GridCell>
          );
        })}
      </GridCells>
    </>
  );
}

// ----------------------------------------------------------------------
// Agenda check (B49, DES 02-11-2026): the person's ticks from the recorder
// beside the AI's verdict. The tick is theirs and is never changed; when the
// two disagree both show, quietly ("Ticked · AI found no discussion"). A
// ticked item's time opens that moment of the recording, like a bookmark.
// Read only: nothing writes ticks after the upload. Anyone but the owner gets
// the ticked items only, with no AI verdict (`ownerView: false`): the rest
// comes from the owner's private agenda note (ADR-023).
// Related: docs/product/agenda-checks-b49.md, shapeAgendaCheck (agendaCheckView.ts)
// ----------------------------------------------------------------------
function AgendaCheckTab({
  meeting,
  contentRtl,
}: {
  meeting: MeetingDetail;
  contentRtl: boolean;
}) {
  const view = shapeAgendaCheck(meeting.agenda_check);
  const recordingUrl = meeting.recording_url;
  const totalDuration = Number(meeting.total_duration_seconds ?? 0);
  const segmentStarts = buildSegmentStarts(meeting.transcript);
  const coverage = meeting.agenda_coverage ?? [];
  const multiNote = new Set(view.items.map((c) => c.noteId).filter(Boolean)).size > 1;
  const judged = view.items.filter((c) => c.status);
  const count = (st: CoverageStatus) => judged.filter((c) => c.status === st).length;
  const counts = {
    covered_decided: count("covered_decided"),
    discussed_open: count("discussed_open"),
    not_raised: count("not_raised"),
  };

  return (
    <>
      <GridBand className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <AgendaFlag className="h-4 w-4 text-brand" />
            {view.ownerView ? "Agenda" : "Agenda ticked during the meeting"}
          </h3>
          <SectionMeta
            stats={[
              ...(view.ownerView
                ? [{ value: `${view.tickedCount}/${view.total}`, label: "ticked", tone: "default" as const }]
                : [{ value: view.tickedCount, label: "ticked", tone: "default" as const }]),
              ...(Object.keys(COVERAGE_META) as CoverageStatus[])
                .filter((st) => counts[st] > 0)
                .map((st) => ({
                  value: counts[st],
                  label: COVERAGE_META[st].label.toLowerCase(),
                  tone: COVERAGE_META[st].tone,
                })),
            ]}
          />
        </div>
        <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">
          {view.ownerView
            ? judged.length > 0
              ? "The points from your agenda, in the order you wrote them. Your ticks from the recording sit beside what was found in what was said. Click a tick's time to hear that moment, or a point to see where it was said."
              : "The points from your agenda and what you ticked while recording. Click a tick's time to hear that moment. What was actually discussed shows here once the meeting is processed."
            : "Agenda items the organiser ticked while recording. Click one to hear that moment."}
        </p>
        {judged.length > 0 ? (
          <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
            <div
              className="bg-success"
              style={{ width: `${(counts.covered_decided / judged.length) * 100}%` }}
            />
            <div
              className="bg-warning"
              style={{ width: `${(counts.discussed_open / judged.length) * 100}%` }}
            />
          </div>
        ) : null}
      </GridBand>

      <GridCells className="grid-cols-1">
        {view.items.map((c, i) => {
          const showHeading = multiNote && (i === 0 || view.items[i - 1].noteId !== c.noteId);
          // The AI's own evidence: the matching coverage point's anchor, if any.
          const cov = c.status
            ? coverage.find((p) => p?.noteId === c.noteId && (p?.point ?? "").trim() === c.text.trim())
            : null;
          return (
            <GridCell key={i} padded={false}>
              {showHeading ? (
                <p className="px-4 pt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground sm:px-5">
                  {c.noteTitle || "Agenda"}
                </p>
              ) : null}
              <AgendaCoverageRow
                point={c.text}
                answer={c.answer}
                status={c.status}
                anchor={
                  c.status
                    ? resolveVoiceAnchor(c.aiTimeInSecs, cov?.anchor ?? null, segmentStarts, totalDuration)
                    : null
                }
                recordingUrl={recordingUrl}
                rtl={contentRtl || isRTLText(c.text)}
                rawAnchor={cov?.anchor ?? null}
                transcript={meeting.transcript}
                evidenceKey={`${meeting.id}:agendacheck:${i}`}
                tick={
                  c.notInRecorder
                    ? null
                    : {
                        ticked: c.ticked,
                        label: c.tickedAt,
                        anchor:
                          c.ticked && c.tickedAtSecs != null
                            ? resolveVoiceAnchor(c.tickedAtSecs, null, segmentStarts, totalDuration)
                            : null,
                      }
                }
                match={c.match}
              />
            </GridCell>
          );
        })}
      </GridCells>
    </>
  );
}

type ShapedKeyPoint = {
  point: string;
  topic: string | null;
  timeInSecs: number | null;
  anchor: MeetingEvidenceAnchor | null;
};

/** Key points ready to render: in time order when they have no topics (as
 *  before 13-10-2026), else in the stored topic order (keyPointGroups.ts). */
function shapeKeyPoints(raw: MeetingDetail["key_points"]): ShapedKeyPoint[] {
  const list: ShapedKeyPoint[] = (raw ?? [])
    .map((kp) => ({
      point: kp?.point ?? "",
      topic: kp?.topic ?? null,
      timeInSecs: typeof kp?.timeInSecs === "number" ? kp.timeInSecs : null,
      anchor: kp?.anchor ?? null,
    }))
    .filter((kp) => !!kp.point);
  if (groupKeyPoints(list).grouped) return list;
  return list.sort((a, b) => {
    if (a.timeInSecs == null && b.timeInSecs == null) return 0;
    if (a.timeInSecs == null) return 1;
    if (b.timeInSecs == null) return -1;
    return a.timeInSecs - b.timeInSecs;
  });
}

/** The groups with each one's position in the flat list (row keys, indexes). */
function keyPointSections(points: ShapedKeyPoint[]): { topic: string | null; items: ShapedKeyPoint[]; offset: number }[] {
  let offset = 0;
  return groupKeyPoints(points).groups.map((g) => {
    const section = { topic: g.topic, items: g.items, offset };
    offset += g.items.length;
    return section;
  });
}

/** U4: the topic sections with their layout keys, keyed ones in the user's
 *  order; plain headings and the no-topic points keep their place. */
function layoutKeyPointSections<T extends { topic: string | null }>(sections: T[], ctl: TabLayoutControl) {
  const keys = pointsSectionKeys(sections.map((g) => g.topic));
  return placeKeyed(
    sections.map((item, i) => ({ key: keys[i], item })),
    ctl.ordered,
  );
}

export function KeyPointsTab({
  meeting,
  contentRtl = false,
}: {
  meeting: MeetingDetail;
  contentRtl?: boolean;
}) {
  // Grouped under topic headings when the points carry topics (13-10-2026,
  // keyPointGroups.ts); a meeting without topics stays the flat list in time
  // order, as before.
  const keyPoints = shapeKeyPoints(meeting.key_points);

  if (keyPoints.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <EmptyTab
          icon={Lightbulb}
          title="No key points yet"
          hint={
            meeting.status === "ready"
              ? "This meeting was compiled before key points existed."
              : "Key takeaways appear here once intelligence is compiled."
          }
        />
      </div>
    );
  }

  const recordingUrl = meeting.recording_url;
  const totalDuration = Number(meeting.total_duration_seconds ?? 0);
  const segmentStarts = buildSegmentStarts(meeting.transcript);

  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Lightbulb className="h-4 w-4 text-brand" />
          Key points
        </h3>
        <SectionMeta
          stats={[{ value: keyPoints.length, label: "moments", tone: "brand" }]}
          hint="· click a row to see where it was said"
        />
      </GridBand>

      {/* The phone's look (DES 14-10-2026, "phone jaisa bulb wala look do"):
          a bulb per point, a line through the bulbs down each topic, no grid
          lines between rows. A click still plays where it was said. */}
      <div className="px-4 pb-4 pt-2 sm:px-5">
        {keyPointSections(keyPoints).map((group, gi) => (
          <div key={`kpg${group.offset}`} className={gi > 0 ? "mt-5" : undefined}>
            {group.topic ? (
              <h4
                className="mb-1 text-[13px] font-semibold text-app-text-secondary"
                dir={contentRtl || isRTLText(group.topic) ? "rtl" : "ltr"}
              >
                {group.topic}
              </h4>
            ) : null}
            {group.items.map((kp, j) => {
              const i = group.offset + j;
              return (
                <KeyPointTimelineRow
                  key={i}
                  point={kp.point}
                  anchor={resolveVoiceAnchor(kp.timeInSecs, kp.anchor, segmentStarts, totalDuration)}
                  recordingUrl={recordingUrl}
                  isFirst={j === 0}
                  isLast={j === group.items.length - 1}
                  rtl={contentRtl || isRTLText(kp.point)}
                  provenance={provenanceOf(kp.anchor)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

function DecisionsTab({
  meeting,
  contentRtl = false,
}: {
  meeting: MeetingDetail;
  contentRtl?: boolean;
}) {
  // Paired BEFORE anything is dropped — see pairDecisions(). Indexing the
  // parallel anchor array by a post-filter row number is what made a decision
  // play its neighbour's audio. (23-08-2026)
  const decisions = pairDecisions(meeting);
  const risks = meeting.attention_risks?.trim();
  if (decisions.length === 0 && !risks) {
    return (
      <div className="p-4 md:p-6">
        <EmptyTab
          icon={CircleCheck}
          title="No decisions recorded"
          hint="Key decisions and attention points appear here once compiled."
        />
      </div>
    );
  }
  const riskLines = risks ? splitRiskLines(risks) : [];

  // Evidence-anchor resolver — mirrors the mobile controller
  // (useMeetingDetailsContent.resolveVoiceAnchor). `key_decision_stamps` is
  // parallel to `key_decisions`, and `key_decision_anchors` (Slice 2) will be
  // parallel too; whichever exists wins, otherwise the decision is unanchored
  // and shows its ordinal rather than an invented clock.
  const recordingUrl = meeting.recording_url;
  const totalDuration = Number(meeting.total_duration_seconds ?? 0);
  const segmentStarts = buildSegmentStarts(meeting.transcript);
  const resolveDecisionAnchor = (d: PairedDecision): VoiceAnchor | null =>
    resolveVoiceAnchor(d.stampSecs, d.anchorRaw, segmentStarts, totalDuration);

  return (
    <>
      {decisions.length > 0 ? (
        <>
          <GridBand className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CircleCheck className="h-4 w-4 text-brand" />
              Key decisions
            </h3>
            <SectionMeta
              stats={[
                {
                  value: decisions.length,
                  label: "decisions",
                  tone: "brand",
                },
              ]}
              hint="· click a row to see where it was said"
            />
          </GridBand>

          <GridCells className="grid-cols-1">
            {decisions.map((d, i) => (
              <GridCell key={i} padded={false}>
                <StampMomentRow
                  point={d.text}
                  anchor={resolveDecisionAnchor(d)}
                  index={i}
                  recordingUrl={recordingUrl}
                  kind="Decision"
                  rtl={contentRtl || isRTLText(d.text)}
                  provenance={provenanceOf(d.anchorRaw)}
                />
              </GridCell>
            ))}
          </GridCells>
        </>
      ) : null}

      {risks ? (
        <>
          <GridBand className="flex flex-wrap items-center justify-between gap-2 bg-warning/[0.06]">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {/* CircleAlert, matching the mobile Intel tab's "Needs
                  attention" callout head — the triangle is reserved for the
                  medium-risk chip, exactly as on mobile. (02-08-2026) */}
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-warning/15 text-warning">
                <CircleAlert className="h-3.5 w-3.5" />
              </span>
              Attention &amp; risks
            </h3>
            <SectionMeta
              stats={[
                {
                  value: Math.max(riskLines.length, 1),
                  label: "to watch",
                  tone: "warning",
                },
              ]}
            />
          </GridBand>

          {riskLines.length > 0 ? (
            <GridCells className="grid-cols-1">
              {riskLines.map((line, i) => (
                <GridCell key={i} className="bg-warning/[0.04] hover:bg-warning/[0.07]">
                  <div
                    className="flex items-start gap-3"
                    dir={contentRtl || isRTLText(line) ? "rtl" : "ltr"}
                  >
                    <span
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-warning/15 font-mono text-[11px] font-semibold tabular-nums text-warning"
                      aria-hidden
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-warning/80">
                        Watch item
                      </p>
                      <p
                        dir={contentRtl || isRTLText(line) ? "rtl" : "ltr"}
                        className="mt-0.5 text-[15px] leading-relaxed text-foreground/90"
                      >
                        {line}
                      </p>
                    </div>
                  </div>
                </GridCell>
              ))}
            </GridCells>
          ) : (
            <GridBand className="bg-warning/[0.04]">
              <p className="whitespace-pre-line text-[15px] leading-relaxed text-foreground/90">
                {risks}
              </p>
            </GridBand>
          )}
        </>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------------
// Tab: Mindmap — two views over one model:
//   Map  -> MeetingMindmapCanvas (React Flow: pan / zoom / minimap)  [default]
//   List -> the collapsible indented tree below (mobile-parity view)
// The tree builder, per-type visuals and the default-expanded rule moved to
// mindmapGraph.ts (04-08-2026) so both views read the same source; the
// expand/collapse state is owned here and shared, so switching view keeps
// whatever you had open.
// Related: mindmapGraph.ts, MeetingMindmapCanvas.tsx,
//          FRONTEND/.../MeetingDetails/{mindmapUtils.ts,tabs/MeetingMindmapTab.tsx}
// ----------------------------------------------------------------------
function MindmapNodeRow({
  node,
  depth,
  expanded,
  onToggle,
  selectedId,
  onSelect,
}: {
  node: MindmapTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const { Icon, color } = mindmapVisual(node.type);

  return (
    <div>
      {/* A branch row keeps its only job — open/close. A leaf had no action at
          all before, so selecting it costs nothing and gives the list the same
          evidence card the canvas has. (04-08-2026) */}
      <button
        type="button"
        onClick={hasChildren ? () => onToggle(node.id) : () => onSelect(node.id)}
        aria-pressed={hasChildren ? undefined : isSelected}
        // Indent must come out of the row's own width, not on top of it:
        // `w-full` resolves against the PARENT, so `w-full` + `margin-left: 32px`
        // is 32px wider than its container and the highlight bled past the
        // section's right edge. Only the selected/hover background made it
        // visible. (04-08-2026)
        style={{
          marginLeft: depth * 16,
          width: `calc(100% - ${depth * 16}px)`,
        }}
        className={cn(
          "flex items-start gap-2 rounded-md py-1.5 pl-1 pr-2 text-left hover:bg-accent",
          isSelected && "bg-brand-soft hover:bg-brand-soft",
        )}
      >
        <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
          {hasChildren ? (
            isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )
          ) : null}
        </span>
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${color}1A` }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color }} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn("block text-sm", node.type === "root" ? "font-semibold" : "font-medium")}
          >
            {node.label}
          </span>
          {node.type && node.type !== "root" ? (
            <span className="block text-[11px] capitalize text-muted-foreground">{node.type}</span>
          ) : null}
        </span>
      </button>

      {hasChildren && isOpen
        ? node.children.map((child) => (
            <MindmapNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))
        : null}
    </div>
  );
}

type MindmapView = "map" | "list";

function MindmapTab({ meeting }: { meeting: MeetingDetail }) {
  const mindmap = meeting.meeting_mindmap ?? null;
  const nodeCount = mindmap?.nodes?.length ?? 0;
  const hasContent = (mindmap?.nodes ?? []).some((n) => n.type !== "root");

  const [view, setView] = useState<MindmapView>("map");
  const tree = useMemo(() => (mindmap ? buildMindmapTree(mindmap) : []), [mindmap]);
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    mindmap ? defaultExpandedIds(mindmap) : new Set<string>(),
  );
  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  // Collapse-all keeps the roots open — an empty canvas is not a useful state.
  const expandAll = useCallback(() => setExpanded(branchIds(tree)), [tree]);
  const collapseAll = useCallback(() => setExpanded(new Set(tree.map((r) => r.id))), [tree]);

  // Selection + evidence. Owned here rather than in the canvas so the list
  // opens the same card, and so the card survives a view switch. (04-08-2026)
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedNode = useMemo(
    () => (mindmap?.nodes ?? []).find((n) => n.id === selectedId) ?? null,
    [mindmap, selectedId],
  );
  const evidenceIndex = useMemo(() => buildMindmapEvidenceIndex(meeting), [meeting]);
  const evidence = useMemo(
    () => (selectedNode ? resolveMindmapEvidence(selectedNode, evidenceIndex) : null),
    [selectedNode, evidenceIndex],
  );

  if (!mindmap || !hasContent) {
    return (
      <EmptyTab
        icon={Waypoints}
        title="No mindmap"
        hint="A structured mindmap is generated for longer meetings."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
          <Waypoints className="h-3.5 w-3.5" />
          {nodeCount} nodes
        </span>

        {/* Segmented view switch. The list stays reachable on purpose: it is
            the printable / narrow-screen read, and the mobile-parity one. */}
        <div className="inline-flex items-center gap-0.5 rounded-full bg-muted p-0.5">
          {(
            [
              { id: "map" as const, label: "Map", icon: Waypoints },
              { id: "list" as const, label: "List", icon: ListChecks },
            ] satisfies Array<{
              id: MindmapView;
              label: string;
              icon: LucideIcon;
            }>
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              aria-pressed={view === id}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                view === id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "map" ? (
        <MeetingMindmapCanvas
          mindmap={mindmap}
          expanded={expanded}
          onToggle={toggle}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      ) : (
        <div>
          {tree.map((root) => (
            <MindmapNodeRow
              key={root.id}
              node={root}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          ))}
        </div>
      )}

      <MeetingMindmapEvidence
        node={selectedNode}
        evidence={evidence}
        recordingUrl={evidenceIndex.recordingUrl}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Shared empty-tab placeholder
// ----------------------------------------------------------------------
function EmptyTab({ icon: Icon, title, hint }: { icon: LucideIcon; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
      <Icon className="h-8 w-8 text-muted-foreground/50" />
      <p className="text-sm font-medium">{title}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

// ----------------------------------------------------------------------
// Right rail — recording + participants + bookmarks
// ----------------------------------------------------------------------
// ----------------------------------------------------------------------
// "Checked against the transcript" (29-09-2026): what synthesis verification
// removed because the cited words did not say it, and which agenda points it
// corrected. Stored by the backend in `metrics.intelVerification`; absent on
// meetings generated before. Shown so an owner can see the intel was checked
// and nothing was quietly dropped. Related: BACKEND intelVerify.service.mjs
// ----------------------------------------------------------------------
const KIND_LABEL: Record<string, string> = {
  decision: "Decision",
  todo: "To-do",
  key_point: "Key point",
};
const STATUS_LABEL: Record<string, string> = {
  not_raised: "not raised",
  discussed_open: "discussed, not decided",
  covered_decided: "covered",
};

function VerificationReport({ meeting }: { meeting: MeetingDetail }) {
  const [open, setOpen] = useState(false);
  const v = meeting.metrics?.intelVerification;
  if (!v || !v.checked) return null;
  const removed = v.removed ?? [];
  const downgraded = v.downgraded ?? [];
  const changes = removed.length + downgraded.length;
  return (
    <div className="p-4 md:p-5">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <ShieldCheck className="h-4 w-4" />
        Checked against the transcript
      </h3>
      <p className="text-xs leading-5 text-muted-foreground">
        {v.checked} item{v.checked === 1 ? "" : "s"} checked against the words cited for them.{" "}
        {changes === 0
          ? "Nothing needed changing."
          : `${removed.length} removed, ${downgraded.length} agenda point${downgraded.length === 1 ? "" : "s"} corrected.`}
      </p>
      {changes > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mt-2 text-xs font-semibold text-brand hover:underline"
          >
            {open ? "Hide details" : "Show what changed"}
          </button>
          {open ? (
            <ul className="mt-2 space-y-2.5">
              {removed.map((r, i) => (
                <li key={`r${i}`} className="text-xs leading-5">
                  <span className="font-semibold text-foreground">
                    {KIND_LABEL[r.kind ?? ""] ?? "Item"} removed:
                  </span>{" "}
                  <span className="text-foreground/80">{r.text}</span>
                  {r.reason ? (
                    <span className="block text-muted-foreground">{r.reason}</span>
                  ) : null}
                </li>
              ))}
              {downgraded.map((d, i) => (
                <li key={`d${i}`} className="text-xs leading-5">
                  <span className="font-semibold text-foreground">
                    Agenda now {STATUS_LABEL[d.to ?? ""] ?? d.to}:
                  </span>{" "}
                  <span className="text-foreground/80">{d.point}</span>
                  {d.reason ? (
                    <span className="block text-muted-foreground">{d.reason}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** A bookmark's moment as a time into the meeting ("12:05"), not the raw ISO
 *  stamp; the clock time ("16:03") when it cannot be placed. (30-09-2026) */
function bookmarkTime(ts: unknown, startedAt: string | null | undefined): string {
  const t = Date.parse(String(ts ?? ""));
  if (!Number.isFinite(t)) return String(ts ?? "");
  const s = Date.parse(String(startedAt ?? ""));
  if (Number.isFinite(s) && t >= s) {
    const secs = Math.floor((t - s) / 1000);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const ss = String(secs % 60).padStart(2, "0");
    return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
  }
  // `started_at` can be later than the marks (it has read as the upload time
  // on live meetings), so no offset is invented: the clock time, the date
  // being the meeting's own.
  return new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function DetailRail({
  meeting,
  columns = false,
  canAddEmail = false,
}: {
  meeting: MeetingDetail;
  /** Player, participants and bookmarks side by side (the phone-style
   *  detail's footer, DES 30-09-2026); the verification report below. */
  columns?: boolean;
  /** Owner (B48): "Add email" on a guest who has a name only. */
  canAddEmail?: boolean;
}) {
  const [addingEmailFor, setAddingEmailFor] = useState<string | null>(null);
  const addGuestEmail = useAddGuestEmail();
  // Prefer the derived merged attendee list (resolved speakers ∪ roster) so a
  // client assigned after the meeting shows up; fall back to the raw roster for
  // older cached responses that predate the field. (08-07-2026)
  const participants = meeting.attendees ?? meeting.participants ?? [];
  const bookmarks = meeting.bookmarks ?? [];
  const player = (
    <div className="min-w-0 p-4 md:p-5">
      <AudioPlayerCard
        src={meeting.recording_url}
        title="Meeting recording"
        subtitle={
          meeting.total_duration_seconds
            ? formatDurationSeconds(Number(meeting.total_duration_seconds))
            : undefined
        }
        className="rounded-none border-0 bg-transparent p-0"
      />
      {!meeting.recording_url ? (
        <p className="text-sm text-muted-foreground">No recording available.</p>
      ) : null}
    </div>
  );
  const people =
    participants.length > 0 ? (
      <div className="min-w-0 p-4 md:p-5">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
          <Users className="h-4 w-4" />
          Participants
          <span className="text-muted-foreground">({participants.length})</span>
        </h3>
        <ul className="space-y-2">
          {participants.map((p, i) => (
            <li key={p.id ?? i} className="flex items-center gap-2">
              <Avatar
                src={p.profileImageUrl}
                initials={getInitials(p.name, null, p.email)}
                alt=""
                size="sm"
                tone="brand"
              />
              <div className="min-w-0 flex-1">
                {/* B48: never an email in the name slot; a guest with no
                    real email reads "Guest" (DES 02-11-2026). */}
                <p className="truncate text-sm">{p.name?.trim() || (p.emailHint ? "Guest" : p.email) || "Participant"}</p>
                {/* The server also hints "guest" on a company member heard
                    only as a speaker (no email on the track): not a guest. */}
                {p.emailHint === "guest" && p.isExternal !== false ? (
                  <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    Guest
                    {canAddEmail && meeting.id && p.name?.trim() ? (
                      <DropdownMenu
                        open={addingEmailFor === (p.id ?? p.name)}
                        onOpenChange={(o) => setAddingEmailFor(o ? (p.id ?? p.name ?? null) : null)}
                      >
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="font-medium text-brand hover:underline">
                            · Add email
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-72 p-3">
                          <p className="pb-2 text-sm font-semibold">Add {p.name.trim()}'s email</p>
                          <div onKeyDown={(e) => e.key !== "Escape" && e.stopPropagation()}>
                            <AddPersonForm
                              fixedName={p.name.trim()}
                              submitLabel="Save email"
                              busy={addGuestEmail.isPending}
                              onCancel={() => setAddingEmailFor(null)}
                              onAdd={({ email }) =>
                                addGuestEmail.mutate(
                                  { meetingId: meeting.id as string, name: p.name!.trim(), email },
                                  {
                                    onSuccess: () => {
                                      setAddingEmailFor(null);
                                      toast({ variant: "success", title: "Email added" });
                                    },
                                    onError: (err) =>
                                      toast({
                                        variant: "destructive",
                                        title: "Could not add the email",
                                        description: err.message || undefined,
                                      }),
                                  },
                                )
                              }
                            />
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </p>
                ) : p.isExternal ? (
                  <p className="text-[11px] text-muted-foreground">
                    {p.isRegisteredUser ? "External · Verblizr user" : "External"}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  const marks =
    bookmarks.length > 0 ? (
      <div className="min-w-0 p-4 md:p-5">
        {/* Bookmark, the glyph the mobile Intel meta row puts on the
              "N marks" chip — the rail heading was the one bare one. */}
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
          <Bookmark className="h-4 w-4" />
          Bookmarks
          <span className="text-muted-foreground">({bookmarks.length})</span>
        </h3>
        <ul className="space-y-2">
          {bookmarks.map((b, i) => (
            <li key={b.id ?? i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span className="tabular-nums">{bookmarkTime(b.timestamp, meeting.started_at)}</span>
              <span className="capitalize">{b.type}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  if (columns) {
    return (
      <div className="divide-y divide-border/60">
        {/* Three columns when wide. Narrower (DES 01-10-2026: "jab screen
            choti ho to in section ko two columns main karo"): the player
            across the top, Participants | Bookmarks side by side under it.
            Only a phone's width stacks all three. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <div className="min-w-0 border-b border-border/60 sm:col-span-2 lg:col-span-1 lg:border-b-0 lg:border-r">
            {player}
          </div>
          <div className="min-w-0 border-b border-border/60 sm:border-b-0 sm:border-r">
            {people}
          </div>
          <div className="min-w-0">{marks}</div>
        </div>
        <VerificationReport meeting={meeting} />
      </div>
    );
  }

  return (
    <div className="divide-y divide-border/60">
      {player}
      {people}
      {marks}
      <VerificationReport meeting={meeting} />
    </div>
  );
}

// ----------------------------------------------------------------------
// Back link
// ----------------------------------------------------------------------
function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="gap-1 pl-1 text-muted-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Button>
  );
}

// ----------------------------------------------------------------------
// Phone layout pieces (30-09-2026) — mobile MeetingDetails on the web.
// Sources: FRONTEND/.../MeetingDetails/components/MeetingSectionHeading.tsx,
// MeetingDetailsPillNav.tsx, tabs/MeetingIntelTab.tsx.
// ----------------------------------------------------------------------
const PHONE_TABS: Array<{ id: DetailTab; label: string; icon: LucideIcon }> = [
  { id: "summary", label: "Intel", icon: Sparkles },
  { id: "keypoints", label: "Points", icon: Lightbulb },
  { id: "coverage", label: "Agenda", icon: AgendaFlag }, // DES 08-10-2026: app icon, was ClipboardCheck
  { id: "todos", label: "Tasks", icon: TodoTick },
  { id: "mindmap", label: "Map", icon: Waypoints },
  // "Transcript" on web and desktop: there is room for the full word (DES
  // 01-10-2026); the phone keeps "Script".
  { id: "transcript", label: "Transcript", icon: MessageSquareText },
];

const PHONE_HEADINGS: Record<DetailTab, { title: string; subtitle: string }> = {
  summary: {
    title: "Meeting Intel",
    subtitle: "Auto-generated from this meeting's transcript, speakers, and your live marks.",
  },
  decisions: { title: "Key decisions", subtitle: "" },
  coverage: {
    title: "Agenda",
    subtitle:
      "The points you wrote before the meeting, checked against what was actually discussed.",
  },
  keypoints: {
    title: "Key Points",
    subtitle:
      "Important takeaways beyond decisions and tasks: facts, figures, and concerns raised.",
  },
  mindmap: {
    title: "Mind Map",
    subtitle: "Hierarchical themes, decisions, actions, and risks from this session.",
  },
  todos: {
    title: "Task TODOs",
    subtitle: "Action items mapped automatically. Tick a circle to mark it done.",
  },
  transcript: {
    title: "Transcript",
    subtitle: "Searchable diarized transcript with word correction.",
  },
};

// A Meeting Notes recording (ADR-030, 06-10-2026) has a summary, to-dos and
// the transcript: no key points, agenda check, mind map or PDF minutes. Its
// first tab is the Notes view (MeetingNotesTierView). Same as the phone's
// dock for a Notes meeting (useMeetingDetailsContent `isNotesTier`).
const NOTES_PHONE_TABS: Array<{ id: DetailTab; label: string; icon: LucideIcon }> = [
  { id: "summary", label: "Notes", icon: Sparkles },
  { id: "todos", label: "Tasks", icon: TodoTick },
  { id: "transcript", label: "Transcript", icon: MessageSquareText },
];
const NOTES_TAB_IDS = new Set<DetailTab>(NOTES_PHONE_TABS.map((t) => t.id));

const NOTES_SUMMARY_HEADING = {
  title: "Meeting notes",
  // The phone's words (03806ae, DES 06-10-2026); the "only you" line the
  // Notes view used to repeat now lives here alone (2cc8908).
  // Sharing v2 (DES 21-10-2026): a Notes meeting can be shared, so "only
  // you" is said of the owner's own typed notes alone.
  subtitle: "A short summary of what was said, and your own notes beside it. Only you see your own notes.",
};
/** Someone the Notes meeting is shared with: no typed notes of theirs here. */
const NOTES_SUMMARY_HEADING_VIEWER = {
  title: "Meeting notes",
  subtitle: "A short summary of what was said.",
};

const PHONE_CHIP_CLS =
  "inline-flex items-center gap-1 rounded-full bg-black/[0.04] px-2 py-1 text-xs font-semibold text-app-text-secondary dark:bg-white/[0.06]";

function PhoneChip({
  icon: Icon,
  onClick,
  children,
}: {
  icon: LucideIcon;
  onClick?: () => void;
  children: ReactNode;
}) {
  const cls = PHONE_CHIP_CLS;
  const inner = (
    <>
      <Icon className="h-3.5 w-3.5" />
      {children}
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(cls, "transition-colors hover:bg-black/[0.08]")}
    >
      {inner}
    </button>
  ) : (
    <span className={cls}>{inner}</span>
  );
}

/** A decision / point row: glyph, text, provenance. A click opens where it
 *  was said under the row (DES 05-10-2026, the phone's press-and-hold sheet
 *  done in place); there is no play chip to hit by accident. */
/**
 * The meeting review's timeline (the phone's IntelTimelineRail, DES
 * 14-10-2026, "key points ko timeline style do", then "do the same in
 * todos"; web 21-10-2026): a 2px line down the list through each row's own
 * icon (bulb, checkbox). It stops at the first and last icon and breaks a
 * little around each one, so it never shows through a hover tint. Absolute in
 * the row; the row must be `relative`. `x` is the icon's centre from the
 * row's start edge, `top`/`bottom` its edges from the row's top.
 */
function TimelineRail({
  first,
  last,
  rtl,
  x,
  top,
  bottom,
}: {
  first: boolean;
  last: boolean;
  rtl: boolean;
  x: string;
  top: string;
  bottom: string;
}) {
  if (first && last) return null;
  const side = rtl ? { right: `calc(${x} - 1px)` } : { left: `calc(${x} - 1px)` };
  const line = "pointer-events-none absolute w-[2px] rounded-full bg-app-quaternary-dark";
  return (
    <>
      {first ? null : (
        <span aria-hidden className={line} style={{ ...side, top: 0, height: `calc(${top} - 1px)` }} />
      )}
      {last ? null : (
        <span aria-hidden className={line} style={{ ...side, top: `calc(${bottom} + 1px)`, bottom: 0 }} />
      )}
    </>
  );
}

function PhoneMomentRow({
  icon: Icon,
  text,
  rtl,
  anchor,
  rawAnchor,
  transcript,
  recordingUrl,
  provenance,
  evidenceKey,
  rail,
  onShare,
}: {
  /** Place in a timeline list (Key Points); none = no line. */
  rail?: { first: boolean; last: boolean };
  /** B50: a Share button that copies this one item as text (decisions). */
  onShare?: () => void;
  icon: LucideIcon;
  text: string;
  rtl: boolean;
  anchor: VoiceAnchor | null;
  rawAnchor: unknown;
  transcript: MeetingDetail["transcript"];
  recordingUrl: string | null | undefined;
  provenance: ItemProvenance;
  /** Unique on the page (meeting id + kind + position): one open at a time. */
  evidenceKey: string;
}) {
  const [open, toggle, close] = useEvidenceOpen(evidenceKey);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      }}
      title={open ? undefined : "Show where this was said"}
      // Hover tint only while closed: open, the evidence panel has its own
      // fill and the two stacked read as a double background (DES 05-10-2026).
      className={cn(
        "group relative -mx-2 cursor-pointer rounded-xl px-2 py-[5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        !open && "hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
      )}
    >
      {/* Icon: 18px at 8px in, 2px below the 5px padding. */}
      {rail ? <TimelineRail first={rail.first} last={rail.last} rtl={rtl} x="17px" top="7px" bottom="25px" /> : null}
      <div className="flex items-start gap-2" dir={rtl ? "rtl" : "ltr"}>
        <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-app-text" />
        <div className="min-w-0 flex-1 space-y-1.5">
          {/* 24 → 26 for Latin, the phone's 22 → 24 (DES 08-10-2026). */}
          <p className={cn("text-base text-app-text", rtl ? "leading-6" : "leading-[26px]")}>
            {text}
          </p>
          {provenance ? <ProvenanceTag kind={provenance} /> : null}
        </div>
        {onShare ? (
          // Its own click only: the row's click opens the evidence.
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShare();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            title="Copy this decision as text, to paste into an email or a chat"
            aria-label="Share this decision as text"
            className="-my-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-opacity hover:bg-black/[0.06] hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-white/[0.08] md:opacity-0 md:group-hover:opacity-100"
          >
            <Share2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <EvidenceCollapse open={open}>
        <MeetingEvidenceInline
          anchor={anchor}
          rawAnchor={rawAnchor}
          transcript={transcript}
          recordingUrl={recordingUrl}
          onClose={close}
          className="mb-1 ms-[26px]"
        />
      </EvidenceCollapse>
    </div>
  );
}

// ----------------------------------------------------------------------
// Recovery when intelligence failed or came back empty (05-10-2026, phone
// parity: MeetingDetailsStatusPanels ProcessingErrorPanel and
// EmptyDataFallbackPanel). The fix used to live only in the ⋯ menu; the
// panel now carries it. No transcript -> reprocess the recording; transcript
// but compile failed, or a ready meeting with nothing extracted -> compile
// again. Each is one paid run, the same as the menu item.
// ----------------------------------------------------------------------
type IntelRecovery = "reprocess" | "recompile" | "empty" | "no_speech" | "timeout" | null;

function intelRecoveryFor(m: MeetingDetail): IntelRecovery {
  const hasTranscript = (m.transcript ?? []).length > 0;
  // A stuck meeting the server timed out, or one with no speech (18-10-2026):
  // both retry by reprocessing the recording.
  const reason = failureReasonOf(m);
  if (reason === "no_speech" || reason === "timeout") return reason;
  if (m.status === "error") return hasTranscript ? "recompile" : "reprocess";
  if (m.status !== "ready") return null;
  const summary = (m.meeting_summary ?? m.summary_overview ?? "").trim();
  const nothing =
    !summary &&
    (m.key_decisions ?? []).filter(Boolean).length === 0 &&
    (m.todos ?? []).length === 0 &&
    (m.key_points ?? []).filter((kp) => !!kp?.point).length === 0;
  return nothing ? "empty" : null;
}

const RECOVERY_COPY = {
  reprocess: {
    title: "Processing failed",
    hint: "Speaker separation failed before a transcript was produced. Retry processing the recording.",
    action: "Retry processing",
  },
  recompile: {
    title: "Compile failed",
    hint: "Something went wrong while compiling the minutes. Retry to generate the intelligence again.",
    action: "Retry compile",
  },
  ...FAILURE_COPY,
  empty: {
    title: "No intelligence compiled",
    hint: "We could not find any decisions or action items for this recording.",
    action: "Compile meeting now",
  },
} as const;

export function IntelRecoveryPanel({
  meeting,
  kind,
  scope,
  canAct,
}: {
  meeting: MeetingDetail;
  kind: Exclude<IntelRecovery, null>;
  scope: MeetingActionsScope;
  /** Owner or super admin: the API refuses anyone else. */
  canAct: boolean;
}) {
  const regenCompany = useRegenerateCompanyMeetingIntelligence();
  const reprocessCompany = useReprocessCompanyMeetingDiarization();
  const regenAdmin = useRegenerateAdminMeetingIntelligence();
  const reprocessAdmin = useReprocessAdminMeetingDiarization();
  const isAdmin = scope === "admin";
  const regen = isAdmin ? regenAdmin : regenCompany;
  const reprocess = isAdmin ? reprocessAdmin : reprocessCompany;
  const busy = regen.isPending || reprocess.isPending;
  const copy = RECOVERY_COPY[kind];
  // "Compile meeting now" on a ready meeting is a paid re-run, not a retry:
  // Super Admin only (DES 22-10-2026). Failed meetings keep their Retry.
  const showAction = canAct && (isAdmin || kind !== "empty");

  const run = async () => {
    if (!meeting.id) return;
    try {
      if (kind === "reprocess" || kind === "no_speech" || kind === "timeout") await reprocess.mutateAsync(meeting.id);
      else await regen.mutateAsync({ id: meeting.id, force: true });
      toast({ variant: "success", title: "Started. This can take a minute or two." });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Retry failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className="mb-6 flex flex-col items-center rounded-[20px] bg-app-card px-6 py-8 text-center">
      {kind === "empty" ? (
        <FileText className="mb-3 h-10 w-10 text-app-text-secondary" />
      ) : (
        <TriangleAlert className="mb-3 h-10 w-10 text-warning" />
      )}
      <p className="text-[17px] font-semibold text-app-text">{copy.title}</p>
      <p className="mt-1 max-w-md text-[15px] leading-[22px] text-app-text-secondary">{copy.hint}</p>
      {showAction ? (
        <Button variant="outline" className="mt-4 rounded-full" onClick={run} disabled={busy}>
          {busy ? (
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4 text-brand" />
          )}
          {copy.action}
        </Button>
      ) : null}
    </div>
  );
}

// Session invoice PDF (05-10-2026, phone parity: MeetingIntelTab
// "Download Session Invoice PDF"). The API sends `invoice_url` only to whoever
// may see this meeting's billing (canViewBoardroomInvoiceBilling), so its
// presence is the permission; the PDF is fetched by meeting id.
export function MeetingInvoiceButton({ meetingId }: { meetingId: string }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await apiClient.get(`${invoiceEndpoints.byId(meetingId)}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boardroom_invoice_${meetingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Could not fetch the session invoice PDF. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" className="mb-6 w-full rounded-full" onClick={download} disabled={busy}>
      {busy ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <ArrowDownToLine className="mr-2 h-4 w-4" />}
      Download session invoice PDF
    </Button>
  );
}

// ----------------------------------------------------------------------
// MeetingDetailView — the loaded-meeting body, shared by both scopes.
// ----------------------------------------------------------------------
interface MeetingDetailViewProps {
  meeting: MeetingDetail;
  scope: MeetingActionsScope;
  /** Back-link handler; also used as the post-delete redirect. */
  onBack: () => void;
  /** Text for the back link (e.g. "Meeting Intel"). */
  backLabel: string;
  /** Optional header line (super admin injects owner + company here). */
  attribution?: ReactNode;
  /** Optional actions next to the manage menu (e.g. feedback prompt). */
  headerExtra?: ReactNode;
  /** Flush grid bands rendered above the score strip while a meeting is
   *  `resolved_pending`. The panel must emit GridBand / GridCells — this
   *  view does not wrap it in another band. (25-09-2026) */
  resolvePanel?: ReactNode;
  /** `phone` = the app's Meeting Details on the web (30-09-2026, DES: web and
   *  desktop get the phone's experience). Every page uses it since
   *  01-10-2026, the super-admin page too (DES: "system admin is not seeing
   *  the same meeting intel screen as others"); `panel` is the older look. */
  layout?: "panel" | "phone";
  /** The built-in demo meeting (calm Home, DES 19-10-2026): sample data, no
   *  server. Read-only, nothing fetched for it, and Ask answers from the
   *  sample (`askPanel`) instead of the Ask endpoint. */
  demo?: { askPanel: ReactNode };
}

export function MeetingDetailView({
  meeting,
  scope,
  onBack,
  backLabel,
  attribution,
  headerExtra,
  resolvePanel,
  layout = "panel",
  demo,
}: MeetingDetailViewProps) {
  // `?tab=todos` opens on Action Items: the "New task for you" notification on
  // the desktop app links here (DESKTOP src/notifications.js, DES review
  // 09-10-2026). Re-read on every navigation, so a click while this page is
  // already open on another meeting still lands on the tab.
  const routeLocation = useLocation();
  const tabFromUrl: DetailTab | null =
    new URLSearchParams(routeLocation.search).get("tab") === "todos" ? "todos" : null;
  const [tab, setTab] = useState<DetailTab>(tabFromUrl ?? "summary");
  useEffect(() => {
    if (tabFromUrl) setTab(tabFromUrl);
  }, [tabFromUrl, routeLocation.key]);
  // Shell chrome state, so the sticky tab bar follows the header collapse.
  const { chromeCollapsed, headerHeight } = useMainScroll();
  const {
    displayMeeting,
    languagePills,
    selectLanguage,
    isTranslating,
    contentRtl,
    showLanguagePills,
    selectedLanguage,
  } = useMeetingMomLanguage(meeting, scope);

  // Transcript stays original (DES 15-10-2026, ADR-037): the stored rows as
  // spoken, no language pills, no translated view.
  const transcriptRows = useMemo(() => meeting.transcript ?? [], [meeting.transcript]);

  // Transcript editing (29-09-2026): owner-only server-side, ready meetings.
  const [edit, setEdit] = useState<{
    mode: TranscriptEditMode;
    selection: TranscriptSelection;
  } | null>(null);

  const speakerMeta = useMemo(
    () => buildSpeakerMetaMap(displayMeeting.resolved_speakers, displayMeeting.participants),
    [displayMeeting.resolved_speakers],
  );
  // Participants' names in speaking order, as the transcript speaker filter
  // lists them, for the Overview highlights (DES trial 08-10-2026).
  const participantTerms = useMemo(
    () =>
      participantNameTerms(
        buildTranscriptSpeakerStats(transcriptRows, speakerMeta, 0).map((s) => s.name),
      ),
    [transcriptRows, speakerMeta],
  );

  // Notes the owner attached to this meeting. Owner-only server-side, and the
  // hook resolves a non-owner's 404 to an empty list, so no visibility rule is
  // re-derived here either. (21-08-2026, ADR-023)
  const { data: attachedNotes } = useMeetingAttachedNotes(meeting.id, !demo);
  // Change a whole speaker (02-10-2026): the owner or a company admin, never
  // the platform super admin's read-only view. The server enforces the same.
  const { user: viewer } = useAuth();
  const speakerSwap = {
    meetingId: meeting.id,
    canSwap:
      scope !== "admin" &&
      displayMeeting.status === "ready" &&
      (meeting.is_owner !== false || isCompanyAdminRole(viewer?.role)),
    swaps: (displayMeeting.speaker_swaps ?? []) as SwapAudit[],
    undoId: displayMeeting.speaker_swap_undo_id ?? null,
  };
  // Sharing v2 (DES 21-10-2026, decision 4): a company admin reads a
  // member's meeting's access list and can remove a share. The server
  // decides (404 for anyone else): the pill shows only once it answers.
  const adminAccess = useMeetingAccess(
    meeting.id,
    !demo && scope !== "admin" && meeting.is_owner === false && isCompanyAdminRole(viewer?.role),
  );
  const isAccessAdmin = adminAccess.data?.viewerRole === "admin";
  // ADR-051 (21-10-2026): exports off for this member. The super admin's
  // read-only scope is never limited.
  const meetingExport = useMeetingExport(displayMeeting);
  const exportOk = scope === "admin" || meetingExport.canExport;

  // Agenda coverage exists only for the meeting's owner — the API strips it for
  // everyone else — so its presence IS the visibility rule and this file must
  // not re-derive one. (19-08-2026, ADR-023)
  // B49: the agenda ticked during the meeting also opens it (a shared viewer
  // gets the ticked items only, and no coverage).
  const hasAgendaCoverage =
    (Array.isArray(displayMeeting.agenda_coverage) && displayMeeting.agenda_coverage.length > 0) ||
    (displayMeeting.agenda_check?.items ?? []).length > 0;

  // Meeting Notes v2 (DES 09-10-2026): the stored doc, else (dev builds only)
  // the local preview's, and only when opened from Notes, Meeting notes
  // (`?notesPreview=1`). From Boardrooms the same meeting keeps its full Intel.
  const previewDoc = previewNotesDocFor(meeting.id, hasNotesPreviewFlag(routeLocation.search));
  const notesDoc = displayMeeting.notes_doc ?? previewDoc;

  // Meeting Notes (ADR-030): only the tabs a Notes meeting has. The dev
  // preview shows its meeting as one, the phone's showsAsNotesMeeting.
  const isNotesTier = isNotesTierMeeting(meeting) || previewDoc != null;
  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (t) =>
          (t.id !== "coverage" || hasAgendaCoverage) && (!isNotesTier || NOTES_TAB_IDS.has(t.id)),
      ),
    [hasAgendaCoverage, isNotesTier],
  );

  // A tab that disappears — coverage cleared by a regenerate, or the row
  // reloaded for a different viewer — must not leave the panel showing nothing.
  useEffect(() => {
    if (tab === "coverage" && !hasAgendaCoverage) {
      setTab("summary");
    }
  }, [tab, hasAgendaCoverage]);

  const isReady = displayMeeting.status === "ready";
  const isBusy =
    displayMeeting.status === "processing" ||
    displayMeeting.status === "synthesis_processing" ||
    displayMeeting.status === "uploading";

  // ---- Phone layout state (hooks run for both layouts) ----
  const { chromeCollapsed: phoneChromeCollapsed, setFlushBottom } = useMainScroll();
  const phoneTopRef = useRef<HTMLDivElement | null>(null);
  const phoneNotesRef = useRef<HTMLDivElement | null>(null);
  // B48: "Add a post-meeting note" from Edit meeting (the agenda is locked
  // after the meeting) lands on the notes section.
  const scrollToNotes =
    (routeLocation.state as { scrollTo?: string } | null)?.scrollTo === "notes";
  useEffect(() => {
    if (!scrollToNotes) return;
    setTab("summary");
    // The section mounts once the meeting has drawn; try for a few seconds.
    let tries = 0;
    const t = window.setInterval(() => {
      tries += 1;
      const el = phoneNotesRef.current;
      if (el && el.getBoundingClientRect().height > 0) {
        window.clearInterval(t);
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      } else if (tries > 30) {
        window.clearInterval(t);
      }
    }, 150);
    return () => window.clearInterval(t);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToNotes, meeting.id]);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [tagsMenuOpen, setTagsMenuOpen] = useState(false);
  const tagCatalogue = useMeetingTagCatalogue(tagsMenuOpen);
  const [transcriptExportSignal, setTranscriptExportSignal] = useState(0);
  // Ask this meeting (DES 15-10-2026): the Ask palette, scoped here.
  const [askOpen, setAskOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  // "Name speakers" on a Meeting Notes meeting (22-10-2026, BACKEND 00a84bd).
  const [namingSpeakers, setNamingSpeakers] = useState(false);
  const saveTags = useSaveMeetingTags();
  const exportMom = useExportCompanyMeetingMom();
  // B50: Export / Send (pick the parts; PDF or text) and the one-item Share.
  const [exportSheetOpen, setExportSheetOpen] = useState(false);
  const shareOne = useShareOneItem(meeting.id);
  useEffect(() => {
    if (layout !== "phone") return;
    // The dock meets the footer, as on Home (MainScrollContext).
    setFlushBottom(true);
    return () => setFlushBottom(false);
  }, [layout, setFlushBottom]);
  // The phone's tips icon by the section heading (DES 05-10-2026), one for
  // the page, in web words: rows open on a click here, not a hold.
  const tips = useHintPopover("meeting_intel_tips");

  // U4 (DES 03-11-2026): the Intel and Points tabs' sections collapse and
  // move, remembered per user on the server (intelLayout.ts). `present` is
  // today's order with empty sections included, so they keep their place.
  const intelStore = useIntelLayout(layout === "phone" && !demo);
  const u4Notes = attachedNotes ?? [];
  const u4HasAgenda = u4Notes.some((n) => n.role !== "addendum");
  // The Attached notes card draws when there is a note or a place to attach one.
  const u4NotesCard = !demo && (u4Notes.length > 0 || scope !== "admin");
  const u4Decisions = pairDecisions(displayMeeting).length;
  // U5: the headings drag like a Home section on the phone (useSectionSort.ts).
  const intelCtl = useSectionSort(useTabLayout(
    intelStore,
    "intel",
    INTEL_DEFAULT_ORDER.filter((k) => k !== "agenda" || u4HasAgenda),
    (k) =>
      k === "decisions"
        ? u4Decisions > 0
        : k === "attention"
          ? isReady
          : k === "agenda" || k === "notes"
            ? u4NotesCard
            : true,
    // One card on the web: Agenda and Private notes move together.
    [["agenda", "notes"]],
  ));
  const u4Topics = pointsSectionKeys(
    keyPointSections(shapeKeyPoints(displayMeeting.key_points)).map((g) => g.topic),
  ).filter((k): k is string => k !== null);
  const pointsCtl = useSectionSort(useTabLayout(intelStore, "points", u4Topics, () => true));

  if (layout === "phone") {
    // A super admin reads, never edits (canManage): no Share, tags or
    // transcript edits, even when the admin payload carries no `is_owner`.
    const isOwner = !demo && scope !== "admin" && meeting.is_owner !== false;
    const tags = ((displayMeeting as { tags?: string[] | null }).tags ?? []).filter(Boolean);
    const notes = attachedNotes ?? [];
    const agendaNotes = notes.filter((n) => n.role === "prep").length;
    const afterNotes = notes.length - agendaNotes;
    const openTodos = activeTodos(displayMeeting.todos).filter((t) => !t?.completed).length;
    const phoneTabs = isNotesTier
      ? NOTES_PHONE_TABS
      : PHONE_TABS.filter((t) => t.id !== "coverage" || hasAgendaCoverage);
    const activeTab = phoneTabs.some((t) => t.id === tab) ? tab : "summary";
    const heading =
      isNotesTier && activeTab === "summary"
        ? isOwner
          ? NOTES_SUMMARY_HEADING
          : NOTES_SUMMARY_HEADING_VIEWER
        : // B49: someone the meeting is shared with sees only the ticks.
          activeTab === "coverage" && displayMeeting.agenda_check?.ownerView === false
          ? { title: "Agenda", subtitle: "What the organiser ticked off during the meeting." }
          : PHONE_HEADINGS[activeTab];
    const canEditTranscript =
      isOwner && displayMeeting.status === "ready";
    // Only what a mouse does that the page doesn't show; the Map has nothing
    // hidden, so no icon there (as on the phone).
    const tabTips: string[] =
      activeTab === "summary"
        ? isNotesTier && notesDoc
          ? ["Click a point to see where it was said and play it."]
          : ["Click a decision to see where it was said and play it."]
        : activeTab === "keypoints"
          ? ["Click a point to see where it was said and play it."]
          : activeTab === "coverage"
            ? ["Click a point to see where it was discussed."]
            : activeTab === "todos"
              ? demo
                ? ["Click a task to see where it was promised."]
                : ["Click the circle to mark a task done.", "Click a task to see where it was promised."]
              : activeTab === "transcript"
                ? [
                    ...(canEditTranscript
                      ? ["Select words to correct them or change who said it."]
                      : []),
                    // The faces strip shows only with two or more speakers.
                    ...(buildTranscriptSpeakerStats(transcriptRows, speakerMeta, 0).length > 1
                      ? ["Click the faces above the transcript to show one person's lines."]
                      : []),
                    ...(transcriptRows.length >= TRANSCRIPT_SCRUBBER_MIN_TURNS
                      ? ["Drag the handle on the right to jump through a long transcript."]
                      : []),
                  ]
                : [];
    const overview = displayMeeting.meeting_overview?.trim();
    const summary = (
      displayMeeting.meeting_summary ??
      displayMeeting.summary_overview ??
      ""
    ).trim();
    const decisions = pairDecisions(displayMeeting);
    // B50: a staff reader (admin scope) and the demo never export.
    const canShareItems = !demo && scope !== "admin" && isReady && exportOk;
    const phoneKeyPoints = shapeKeyPoints(displayMeeting.key_points);
    const risks = displayMeeting.attention_risks?.trim();
    const recovery = intelRecoveryFor(displayMeeting);
    const segmentStarts = buildSegmentStarts(displayMeeting.transcript);
    const totalDuration = Number(displayMeeting.total_duration_seconds ?? 0);
    const mmss = totalDuration
      ? `${Math.floor(totalDuration / 60)}:${String(Math.floor(totalDuration % 60)).padStart(2, "0")}`
      : "—";

    const goTab = (next: DetailTab) => {
      setTab(next);
      phoneTopRef.current?.scrollIntoView({ block: "start" });
    };
    const handleExportMom = async () => {
      try {
        const res = await exportMom.mutateAsync({
          id: meeting.id,
          ...(selectedLanguage ? { targetLanguage: selectedLanguage } : {}),
        });
        if (res.momPdfUrl) window.open(res.momPdfUrl, "_blank", "noopener");
        toast({ variant: "success", title: isNotesTier ? "Notes PDF ready" : "MoM PDF ready" });
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Export failed",
          description: err instanceof ApiClientError ? err.message : "Please try again.",
        });
      }
    };

    // The existing tab bodies are built from grid bands; inside a phone card
    // their right-hand rules go, the row rules stay.
    // No cards (DES 30-09-2026: "jitnay bhi sections hain inhain cards main
    // na rakho"): the reused web bodies sit flush on the page like the phone's
    // sections. Their grid bands keep the row rules but lose the right-hand
    // ones, and the negative margin cancels their own side padding so text
    // lines up with the headings.
    // …and no dividers either (DES: "dividers bhi nahin"): every row and
    // band rule inside is dropped. `!` because Tailwind's divide-y selector
    // outranks a descendant override.
    const card =
      "-mx-4 md:-mx-5 [&_.border-r]:border-r-0 [&_.border-b]:!border-b-0 [&_.border-t]:!border-t-0 [&_.divide-y>*]:!border-t-0";

    return (
      // Full width (DES 30-09-2026: "content ki width bari karo, poori space
      // lay"): the phone's column, stretched to the page on web and desktop.
      <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pt-6 md:px-8">
        <div ref={phoneTopRef} className="scroll-mt-24" />
        {/* Header: the phone's CollapsibleHeader row (back, title). */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-app-card shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
          >
            <ArrowLeft className="h-5 w-5 text-app-text" />
          </button>
          <div className="min-w-0 flex-1">
            {/* B48: the owner renames by clicking the title (or ••• > Rename meeting). */}
            <h1
              dir={isRTLText(displayMeeting.title ?? "") ? "rtl" : "ltr"}
              className="truncate text-[22px] font-bold text-app-text"
            >
              {isOwner ? (
                <button
                  type="button"
                  onClick={() => setRenaming(true)}
                  title="Rename meeting"
                  className="max-w-full truncate rounded-md text-left hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {displayMeeting.title?.trim() || "Untitled meeting"}
                </button>
              ) : (
                displayMeeting.title?.trim() || "Untitled meeting"
              )}
            </h1>
            <p className="truncate text-[13px] font-semibold text-app-text-secondary">
              {formatDateTime(displayMeeting.started_at)}
              {displayMeeting.organised_by ? ` · Organised by ${displayMeeting.organised_by}` : ""}
            </p>
            {/* Super admin: whose meeting, which company. */}
            {attribution ? (
              <div className="mt-0.5 text-[13px] text-app-text-secondary">{attribution}</div>
            ) : null}
          </div>
          {displayMeeting.status !== "ready" ? (
            <MeetingStatusBadge status={displayMeeting.status} failureReason={displayMeeting.failure_reason} />
          ) : null}
          <div className="flex shrink-0 items-center gap-2">
            {headerExtra}
            {/* Ask this meeting: the palette opened by the heading's Ask
                mark. Its citations are this meeting, so they link here. */}
            <AskPalette open={askOpen} onOpenChange={setAskOpen} title="Ask AI about this meeting">
              {demo ? (
                demo.askPanel
              ) : (
                <AskMeetingsPanel
                  meetingId={meeting.id}
                  meetingHref={() => `${routeLocation.pathname}${routeLocation.search}`}
                />
              )}
            </AskPalette>
            {demo ? null : (
              <MeetingExportSheet
                open={exportSheetOpen}
                onOpenChange={setExportSheetOpen}
                meetingId={meeting.id}
              />
            )}
            {isOwner ? (
              <MeetingRenameDialog
                open={renaming}
                onOpenChange={setRenaming}
                meetingId={meeting.id}
                title={displayMeeting.title ?? ""}
              />
            ) : null}
            {/* The demo has nothing to delete, share or export. */}
            {demo ? null : (
              <MeetingRowActions
                meeting={displayMeeting}
                scope={scope}
                onDeleted={onBack}
                onRename={isOwner ? () => setRenaming(true) : undefined}
                // U5: Intel + Points back to today's layout (both tabs);
                // a Notes meeting has neither tab.
                onResetLayout={isNotesTierMeeting(meeting) ? undefined : intelStore.resetAll}
                resetLayoutDisabled={intelStore.layout === null}
                targetLanguage={selectedLanguage}
                transcriptExportSignal={transcriptExportSignal}
                // Owner only, as Boardroom's speaker naming (the server
                // answers 404 to anyone else).
                onNameSpeakers={
                  isOwner &&
                  isNotesTierMeeting(meeting) &&
                  (displayMeeting.resolved_speakers?.length ?? 0) > 0
                    ? () => {
                        setTab("summary");
                        setNamingSpeakers(true);
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* Section heading (MeetingSectionHeading) + the Share | Export pill. */}
        {/* Trial (DES 08-10-2026): the drawer heading, its line running to
            the Share | Export pill and tips icon, all centred on that line
            (MEETING_GRID_HEADINGS; false = the old grey title). */}
        <div
          // Held LTR with the drawer heading, the pill always on the left,
          // even in RTL content: only the AI text reads RTL (DES 08-10-2026).
          dir={MEETING_GRID_HEADINGS ? "ltr" : undefined}
          className={cn(
            "mt-6 flex gap-2",
            MEETING_GRID_HEADINGS ? "items-center" : "items-start",
          )}
        >
          <div className="min-w-0 flex-1">
            {MEETING_GRID_HEADINGS ? (
              <GridHeading as="h2" label={heading.title} />
            ) : (
              <h2 className={MEETING_OLD_HEADING_CLS}>{heading.title}</h2>
            )}
          </div>
          {activeTab === "summary" ? (
            <div
              // Its own halves keep Share | Export order and corners inside a
              // mirrored (RTL) row.
              dir="ltr"
              className={cn(
                "inline-flex h-9 shrink-0 items-center rounded-full bg-[#F4F4F2] dark:bg-muted",
                !MEETING_GRID_HEADINGS && "-mt-1.5",
              )}
            >
              {/* Ask this meeting (DES 15-10-2026), icon only as on the
                  phone: anyone who can open the meeting can ask it. */}
              <button
                type="button"
                onClick={() => setAskOpen(true)}
                aria-label="Ask AI about this meeting"
                title="Ask AI about this meeting"
                className={cn(
                  "inline-flex h-9 items-center px-3 hover:bg-black/[0.04]",
                  demo ? "rounded-full" : "rounded-l-full",
                )}
              >
                <AskSpark className="h-[20px] w-[20px] text-brand" />
              </button>
              {demo ? null : <span className="h-[18px] w-px bg-border" />}
              {/* Notes shares like a Boardroom since sharing v2 (DES
                  21-10-2026); a company admin gets the access list on a
                  member's meeting (remove only). */}
              {isOwner || isAccessAdmin ? (
                <>
                  {/* Opens under the pill as a menu, like "⋯" (DES 30-09-2026). */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-9 items-center gap-1.5 px-3.5 text-[15px] font-semibold text-app-text hover:bg-black/[0.04]"
                      >
                        {/* Share2 / ArrowDownToLine, as the phone (DES 08-10-2026,
                            "simple icon", S3 + E1): were UserPlus / FileDown. */}
                        <Share2 className="h-[18px] w-[18px] text-brand" />
                        {isOwner ? "Share" : "Access"}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="p-1">
                      <MeetingShareMenu meetingId={meeting.id} />
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <span className="h-[18px] w-px bg-border" />
                </>
              ) : null}
              {demo ? null : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "inline-flex h-9 items-center gap-1.5 px-3.5 text-[15px] font-semibold text-app-text hover:bg-black/[0.04]",
                      "rounded-r-full",
                    )}
                  >
                    {exportMom.isPending ? (
                      <LoaderCircle className="h-[18px] w-[18px] animate-spin text-brand" />
                    ) : (
                      <ArrowDownToLine className="h-[18px] w-[18px] text-brand" />
                    )}
                    Export
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {/* B50 (DES 02-11-2026): pick the parts, PDF or text. The
                      two one-click PDFs below stay as they were. */}
                  <DropdownMenuItem
                    disabled={!isReady || !exportOk}
                    onSelect={() => deferAfterDropdownMenu(() => setExportSheetOpen(true))}
                  >
                    <Send className="mr-2 h-4 w-4 text-brand" />
                    <span className="flex flex-col">
                      Export / Send…
                      <span className="text-xs text-muted-foreground">Pick the parts · PDF or text</span>
                    </span>
                  </DropdownMenuItem>
                  {/* A Notes meeting exports its notes, not minutes (DES
                      09-10-2026; BACKEND generateMeetingNotesPdf, the same
                      export route). Not for the dev preview of a full
                      meeting, whose server export would be minutes. */}
                  {isNotesTier && !isNotesTierMeeting(meeting) ? null : (
                    <DropdownMenuItem
                      disabled={!isReady || !exportOk}
                      onSelect={() => void handleExportMom()}
                    >
                      <FileText className="mr-2 h-4 w-4 text-brand" />
                      <span className="flex flex-col">
                        {isNotesTier ? "Meeting notes" : "Minutes of Meeting"}
                        <span className="text-xs text-muted-foreground">
                          {isNotesTier ? "PDF" : "PDF · active language"}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    disabled={!exportOk}
                    onSelect={() => setTranscriptExportSignal((n) => n + 1)}
                  >
                    <MessageSquareText className="mr-2 h-4 w-4 text-brand" />
                    <span className="flex flex-col">
                      Transcript
                      <span className="text-xs text-muted-foreground">PDF · original language</span>
                    </span>
                  </DropdownMenuItem>
                  {/* ADR-051: why both are off. */}
                  {exportOk ? null : (
                    <p className="px-2 pb-1.5 pt-0.5 text-xs text-muted-foreground">
                      {meetingExport.exportLine}
                    </p>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </div>
          ) : null}
          {tabTips.length > 0 ? (
            <HintIcon
              open={tips.open}
              onClick={tips.toggle}
              anchorRef={tips.anchorRef}
              className={MEETING_GRID_HEADINGS ? undefined : "-mt-1.5"}
            />
          ) : null}
        </div>
        {/* Between the title and the subtitle, so the line from the icon
            never runs through the subtitle text (as on the phone). */}
        {tips.open && tabTips.length > 0 ? (
          <HintBubble
            key={activeTab}
            lines={tabTips}
            onClose={tips.close}
            anchorRef={tips.anchorRef}
            className="mb-3 mt-1"
          />
        ) : null}
        <p
          dir={MEETING_GRID_HEADINGS ? "ltr" : undefined}
          className="mb-4 mt-1 text-[15px] leading-[22px] text-app-text-secondary"
        >
          {heading.subtitle}
        </p>

        {activeTab === "summary" && isNotesTier ? (
          // Meeting Notes (ADR-030): its own layout. It has no decisions or
          // metrics by design, so none of the full layout's "will appear
          // after intelligence is compiled" placeholders may catch it.
          <>
            {namingSpeakers && isOwner && !isBusy ? (
              <div className={cn(card, "mb-6")}>
                <EmployeeSpeakerResolvePanel
                  key={meeting.id}
                  meeting={displayMeeting}
                  mode="notes"
                  onDone={() => setNamingSpeakers(false)}
                />
              </div>
            ) : null}
            <MeetingNotesTierView
              meeting={displayMeeting}
              isOwner={isOwner}
              viewerId={viewer?.id ?? null}
              attachedNotes={notes}
              contentRtl={contentRtl}
              notesDoc={notesDoc}
            />
            <div className="mb-6 overflow-hidden rounded-[20px] bg-app-card">
              <DetailRail meeting={displayMeeting} columns canAddEmail={isOwner} />
            </div>
          </>
        ) : activeTab === "summary" ? (
          <>
            {recovery ? (
              <IntelRecoveryPanel
                meeting={displayMeeting}
                kind={recovery}
                scope={scope}
                canAct={scope === "admin" || isOwner}
              />
            ) : null}
            {showLanguagePills ? (
              <div
                role="tablist"
                aria-label="Meeting language"
                // The chip tint, no outline (DES 08-10-2026, phone parity):
                // the track = chipPill, the selected pill a soft wash.
                className="mb-3 inline-flex self-start rounded-full bg-black/[0.04] p-[3px] dark:bg-white/[0.06]"
              >
                {languagePills.map((pill) => (
                  <button
                    key={pill.code}
                    type="button"
                    role="tab"
                    aria-selected={pill.active}
                    disabled={isTranslating}
                    onClick={() => void selectLanguage(pill.code)}
                    className={cn(
                      "inline-flex min-w-[88px] items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors",
                      // Not black (DES 08-10-2026): light = the drawer
                      // pill's warm wash, no white under it; dark = white 10%.
                      // Selected text in the primary ink.
                      pill.active
                        ? "bg-[rgba(50,38,18,0.14)] text-app-text dark:bg-white/[0.10]"
                        : "text-app-text-secondary hover:text-app-text",
                    )}
                  >
                    {pill.active && isTranslating ? (
                      <LoaderCircle className="h-3 w-3 animate-spin" />
                    ) : null}
                    {pill.label}
                  </button>
                ))}
              </div>
            ) : null}

            {/* Meta chips: LTR in RTL content too (DES 08-10-2026). */}
            <div dir="ltr" className="mb-6 flex flex-wrap gap-2">
              <PhoneChip icon={Clock}>{mmss}</PhoneChip>
              <PhoneChip
                icon={Bookmark}
              >{`${(displayMeeting.bookmarks ?? []).length} marks`}</PhoneChip>
              {demo ? null : (
              <PhoneChip
                icon={PenLine} // DES 08-10-2026: app icon, was NotebookPen
                onClick={() =>
                  phoneNotesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                {notes.length === 0
                  ? "Add a note"
                  : [
                      agendaNotes ? `${agendaNotes} agenda` : "",
                      afterNotes ? `${afterNotes} after` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
              </PhoneChip>
              )}
              {isOwner ? (
                // Tags as a dropdown under the chip, not a dialog (DES
                // 04-10-2026): find, add with Enter, tap to remove.
                <TagsMenu
                  tags={tagDraft}
                  // Your other meetings' tags, as on the phone (05-10-2026).
                  suggestions={(tagCatalogue.data ?? []).map((tag) => ({ tag, uses: 0 }))}
                  saving={saveTags.isPending}
                  onOpenChange={(o) => {
                    setTagsMenuOpen(o);
                    if (o) setTagDraft(tags);
                  }}
                  onChange={(next) => {
                    const prev = tagDraft;
                    setTagDraft(next);
                    saveTags.mutate(
                      { id: meeting.id, tags: next },
                      {
                        onError: (err) => {
                          setTagDraft(prev);
                          toast({
                            variant: "destructive",
                            title: "Could not save tags",
                            description:
                              err instanceof ApiClientError ? err.message : "Please try again.",
                          });
                        },
                      },
                    );
                  }}
                  anchor={
                    <button
                      type="button"
                      className={cn(PHONE_CHIP_CLS, "transition-colors hover:bg-black/[0.08]")}
                    >
                      <Tag className="h-3.5 w-3.5" />
                  {tags.length === 0
                        ? "Add tags"
                        : `${tags
                            .slice(0, 2)
                            .map((t) => `#${t}`)
                            .join(" ")}${tags.length > 2 ? ` +${tags.length - 2}` : ""}`}
                    </button>
                  }
                />
              ) : tags.length ? (
                <PhoneChip icon={Tag}>
                  {`${tags
                    .slice(0, 2)
                    .map((t) => `#${t}`)
                    .join(" ")}${tags.length > 2 ? ` +${tags.length - 2}` : ""}`}
                </PhoneChip>
              ) : null}
            </div>

            {displayMeeting.metrics ? (
              <MeetingScoreStrip metrics={displayMeeting.metrics} variant="row" className="mb-6" />
            ) : (
              <p className="mb-6 text-center text-sm text-app-text-secondary">
                Analytics will appear once this meeting is fully processed.
              </p>
            )}

            {isBusy ? (
              <p className="mb-6 flex items-center gap-2 rounded-xl bg-info-soft p-3 text-sm text-info">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                This meeting is still being processed. Intelligence will appear once it&apos;s
                ready.
              </p>
            ) : null}
            {resolvePanel ? <div className={cn(card, "mb-6")}>{resolvePanel}</div> : null}

            {/* U4: the sections in the user's order (intelLayout.ts); each
                heading collapses and drags (U5: like a Home section). */}
            {intelCtl.allUnits.map((unit) => {
              const key = unit[0];
              const shut = intelCtl.isCollapsed(key);
              if (unit.includes("notes")) {
                return (
                  /* Notes / agenda: LTR in RTL content too (DES 08-10-2026). */
                  <div
                    key="notes"
                    {...sortUnitAttrs("intel", key)}
                    dir="ltr"
                    ref={phoneNotesRef}
                    className={cn(card, "mb-6 scroll-mt-24", demo && "hidden")}
                  >
                    <AttachedNotesSection
                      meetingId={meeting.id}
                      notes={notes}
                      isMeetingOwner={isOwner}
                      scope={scope}
                      contentRtl={contentRtl}
                      layoutCtl={intelCtl}
                    />
                  </div>
                );
              }
              if (key === "overview") {
                return (
                  <div key={key} {...sortUnitAttrs("intel", key)}>
                    <IntelLayoutHeading ctl={intelCtl} sectionKey={key} label="Overview" collapsedClassName="mb-6" />
                    {shut ? null : (
                      <p
                        dir={contentRtl || isRTLText(overview ?? "") ? "rtl" : "ltr"}
                        className={cn(
                          "mb-6 text-base text-app-text",
                          meetingBodyLeading(contentRtl || isRTLText(overview ?? "")),
                        )}
                      >
                        {/* Participants' first mentions highlighted, Overview only
                            (MEETING_NAME_EMPHASIS trial, DES 08-10-2026). */}
                        {overview ? (
                          renderNameHighlights(overview, participantTerms)
                        ) : (
                          "Overview will appear after intelligence is compiled."
                        )}
                      </p>
                    )}
                  </div>
                );
              }
              if (key === "summary") {
                return (
                  <div key={key} {...sortUnitAttrs("intel", key)}>
                    <IntelLayoutHeading ctl={intelCtl} sectionKey={key} label="Summary" collapsedClassName="mb-6" />
                    {shut ? null : (
                      <div className="mb-6 space-y-3">
                        {summary ? (
                          splitProseParagraphs(summary).map((para, i) => (
                            <p
                              key={i}
                              dir={contentRtl || isRTLText(para) ? "rtl" : "ltr"}
                              className={cn(
                                "text-base text-app-text",
                                meetingBodyLeading(contentRtl || isRTLText(para)),
                              )}
                            >
                              {para}
                            </p>
                          ))
                        ) : (
                          <p className={cn("text-base text-app-text", meetingBodyLeading(contentRtl))}>
                            Summary will appear after intelligence is compiled.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              if (key === "decisions") {
                return decisions.length ? (
                  <div key={key} {...sortUnitAttrs("intel", key)}>
                    <IntelLayoutHeading
                      ctl={intelCtl}
                      sectionKey={key}
                      label="Key decisions"
                      count={decisions.length}
                      collapsedClassName="mb-6"
                    />
                    {shut ? null : (
                      <div className="mb-6">
                        {decisions.map((d, i) => (
                          <PhoneMomentRow
                            key={i}
                            icon={CircleCheck}
                            text={d.text}
                            rtl={contentRtl || isRTLText(d.text)}
                            anchor={resolveVoiceAnchor(
                              d.stampSecs,
                              d.anchorRaw,
                              segmentStarts,
                              totalDuration,
                            )}
                            rawAnchor={d.anchorRaw}
                            transcript={displayMeeting.transcript}
                            evidenceKey={`${meeting.id}:decision:${i}`}
                            recordingUrl={displayMeeting.recording_url}
                            provenance={provenanceOf(d.anchorRaw)}
                            onShare={
                              canShareItems ? () => void shareOne.share("decisions", String(d.index)) : undefined
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : null;
              }
              if (key === "attention") {
                return isReady ? (
                  <div
                    key={key}
                    {...sortUnitAttrs("intel", key)}
                    className="mb-6 rounded-[14px] border border-black/10 bg-black/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]"
                  >
                    {/* The box's own title is its heading: the arrow at its end. */}
                    <IntelLayoutFrame ctl={intelCtl} sectionKey={key} label="Needs attention">
                      <p
                        className={cn(
                          "flex items-center gap-1.5 text-[15px] font-bold text-app-text",
                          !shut && "mb-1",
                        )}
                      >
                        <CircleAlert className="h-[18px] w-[18px]" />
                        <span className="flex-1">Needs attention</span>
                        <SectionChevron collapsed={shut} />
                      </p>
                    </IntelLayoutFrame>
                    {shut ? null : (
                      <p
                        dir={contentRtl || isRTLText(risks ?? "") ? "rtl" : "ltr"}
                        className={cn(
                          "whitespace-pre-line text-[15px] text-app-text",
                          // 23 → 25 for Latin, the phone's 22 → 24 (DES 08-10-2026).
                          contentRtl || isRTLText(risks ?? "") ? "leading-[23px]" : "leading-[25px]",
                        )}
                      >
                        {risks || "Risk highlights will appear after intelligence is compiled."}
                      </p>
                    )}
                  </div>
                ) : null;
              }
              return null;
            })}

            {/* Web extra (not on the phone, which plays clips only): the full
                recording, participants, bookmarks and the verification report. */}
            {/* The one section that stays a card (DES 30-09-2026: "put this
                section back in the card as we did first, just this one"),
                with its hairlines between the columns. */}
            <div className="mb-6 overflow-hidden rounded-[20px] bg-app-card">
              <DetailRail meeting={displayMeeting} columns canAddEmail={isOwner} />
            </div>

            {displayMeeting.invoice_url && meeting.id ? (
              <MeetingInvoiceButton meetingId={meeting.id} />
            ) : null}
          </>
        ) : isReady && activeTab === "mindmap" ? (
          // The phone's tree, flush on the page like the phone (no card).
          <PhoneMindmapTree meeting={displayMeeting} />
        ) : recovery && recovery !== "empty" && activeTab !== "transcript" ? (
          <IntelRecoveryPanel
            meeting={displayMeeting}
            kind={recovery}
            scope={scope}
            canAct={scope === "admin" || isOwner}
          />
        ) : !isReady && activeTab !== "transcript" ? (
          <EmptyTab
            icon={Sparkles}
            title={isNotesTier ? "Notes not ready" : "Intelligence not ready"}
            hint={
              isNotesTier
                ? "Tasks appear once your meeting notes are written."
                : "Key points, tasks and the mind map appear once synthesis completes."
            }
          />
        ) : (
          <div className={cn(card, "mb-6")}>
            {activeTab === "transcript" ? (
              <TranscriptTab
                rows={transcriptRows}
                speakers={speakerMeta}
                totalDuration={totalDuration}
                editable={
                  isOwner &&
                  displayMeeting.status === "ready"
                }
                onEdit={(mode, selection) => setEdit({ mode, selection })}
                swap={speakerSwap}
              />
            ) : activeTab === "todos" ? (
              <TodosTab
                todos={displayMeeting.todos ?? []}
                speakers={speakerMeta}
                meeting={displayMeeting}
                contentRtl={contentRtl}
                readOnly={scope === "admin" || Boolean(demo)}
                onShare={canShareItems ? (id) => void shareOne.share("todos", id) : undefined}
              />
            ) : activeTab === "keypoints" ? (
              // The phone's Points rows: lightbulb, text, provenance, and the
              // web's clip button where the phone has press-and-hold.
              <div className="px-4 md:px-5">
                {phoneKeyPoints.length ? (
                  // Topic headings like Meeting Notes' sections (13-10-2026).
                  // U4: topics in the user's order, each heading collapses
                  // and moves; the points without a topic stay last.
                  layoutKeyPointSections(keyPointSections(phoneKeyPoints), pointsCtl).map(({ key, item: group }) => (
                    <section
                      key={`kpg${group.offset}`}
                      {...sortUnitAttrs("points", key)}
                      className={group.topic ? "pt-4" : undefined}
                    >
                      {group.topic && key ? (
                        <IntelLayoutHeading
                          ctl={pointsCtl}
                          sectionKey={key}
                          label={group.topic}
                          count={group.items.length}
                        />
                      ) : group.topic ? (
                        // A refused or repeated key: today's plain heading (as the phone).
                        <MeetingBlockHeading>{group.topic}</MeetingBlockHeading>
                      ) : null}
                      {/* While a heading drags, every topic shows its heading only (U5). */}
                      {pointsCtl.sort?.activeKey || (key && pointsCtl.isCollapsed(key)) ? null : group.items.map((kp, j) => {
                        const i = group.offset + j;
                        return (
                          <PhoneMomentRow
                            key={i}
                            rail={{ first: j === 0, last: j === group.items.length - 1 }}
                            icon={Lightbulb}
                            text={kp.point}
                            rtl={contentRtl || isRTLText(kp.point)}
                            anchor={resolveVoiceAnchor(kp.timeInSecs, kp.anchor, segmentStarts, totalDuration)}
                            rawAnchor={kp.anchor}
                            transcript={displayMeeting.transcript}
                            evidenceKey={`${meeting.id}:point:${i}`}
                            recordingUrl={displayMeeting.recording_url}
                            provenance={provenanceOf(kp.anchor)}
                          />
                        );
                      })}
                    </section>
                  ))
                ) : (
                  <div className="py-12 text-center">
                    <Lightbulb className="mx-auto mb-2 h-9 w-9 text-app-text-secondary" />
                    <p className="text-[17px] font-semibold text-app-text">No key points yet</p>
                    <p className="mt-1 text-[15px] text-app-text-secondary">
                      Key takeaways will appear here after intelligence is compiled.
                    </p>
                  </div>
                )}
              </div>
            ) : activeTab === "coverage" ? (
              <AgendaCoverageTab meeting={displayMeeting} contentRtl={contentRtl} />
            ) : (
              <div className="p-4 md:p-6">
                <MindmapTab meeting={displayMeeting} />
              </div>
            )}
          </div>
        )}

        {/* The phone's pill dock (MeetingDetailsPillNav): icons, the active
            tab with its label, the open-task count on Tasks. */}
        <div
          className={cn(
            // The phone's dockFadeColors (shared/theme/dockGlass.ts): never
            // solid, so the page shows through the glass (04-10-2026, DES).
            "sticky bottom-0 z-20 -mx-5 mt-auto flex justify-center px-5 pb-3 pt-6 transition-transform duration-300 md:-mx-8 md:px-8",
            dockFade,
            phoneChromeCollapsed && "translate-y-[calc(100%+1rem)]",
          )}
        >
          {/* The dock and, beside it, Ask this meeting as a round glass
              button (DES 20-10-2026; the heading's Ask mark stays too). The
              dock is a little narrower to make room: 3xl -> 2xl. */}
          <div className="flex w-full max-w-lg items-center justify-center gap-2 sm:max-w-3xl">
            <nav
              aria-label="Meeting sections"
              className={cn(
                noteDockGlass,
                // A Notes meeting has 3 tabs: the dock hugs them instead of
                // stretching to the full width (DES 20-10-2026).
                phoneTabs.length <= 3
                  ? "flex h-14 min-w-0 items-center gap-2 px-2"
                  : "flex h-14 min-w-0 flex-1 items-center justify-between gap-1 px-2 sm:max-w-2xl",
              )}
            >
              {phoneTabs.map((t) => {
                const Icon = t.icon;
                const on = activeTab === t.id;
                const badge =
                  t.id === "todos" && openTodos > 0
                    ? openTodos > 9
                      ? "9+"
                      : String(openTodos)
                    : null;
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-label={t.label}
                    aria-current={on ? "page" : undefined}
                    title={t.label}
                    onClick={() => goTab(t.id)}
                    className={cn(
                      "relative flex h-10 items-center justify-center rounded-full transition-colors",
                      // Every label shows from `sm` (DES 30-09-2026: "web per labels
                      // hamesha show hon"); a phone-width window keeps the app's
                      // icon-only inactive tabs.
                      // THE selected dock pill (U3): brand 10% light,
                      // brand 72% + top highlight dark.
                      on
                        ? cn("gap-2 px-3", dockSelected)
                        : "min-w-11 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] sm:gap-2 sm:px-3",
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-[22px] w-[22px]",
                        // Dark: white on the red pill, not red on red (U2,
                        // phone parity); light stays brand.
                        on ? "text-brand dark:text-white" : "text-app-text-secondary",
                      )}
                      strokeWidth={2}
                    />
                    <span
                      className={cn(
                        "text-sm font-semibold",
                        on ? "text-brand dark:text-white" : "hidden text-app-text-secondary sm:inline",
                      )}
                    >
                      {t.label}
                    </span>
                    {badge ? (
                      <span
                        className={cn(
                          "flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[9px] font-bold text-white",
                          on
                            ? "static h-[18px] min-w-[18px]"
                            : "absolute -right-0.5 -top-0.5 border-[1.5px] border-white dark:border-app-page sm:static sm:h-[18px] sm:min-w-[18px] sm:border-0",
                        )}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>
            {/* A real tooltip on hover (DES 20-10-2026), not the browser's. */}
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setAskOpen(true)}
                    aria-label="Ask AI about this meeting"
                    className={cn(
                      noteDockGlass,
                      "inline-flex h-14 w-14 shrink-0 items-center justify-center transition-colors hover:brightness-110",
                    )}
                  >
                    <AskSpark className="h-9 w-9 text-brand" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Ask AI about this meeting</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <TranscriptEditSheet
          open={!!edit}
          onOpenChange={(open) => {
            if (!open) setEdit(null);
          }}
          mode={edit?.mode ?? "correct"}
          meeting={meeting}
          selection={edit?.selection ?? null}
          onDone={() => window.getSelection()?.removeAllRanges()}
        />
      </div>
    );
  }

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="space-y-3">
          <BackLink label={backLabel} onClick={onBack} />

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight">
                  {displayMeeting.title?.trim() || "Untitled meeting"}
                </h2>
                <MeetingStatusBadge status={displayMeeting.status} failureReason={displayMeeting.failure_reason} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="h-4 w-4" />
                  {formatDateTime(displayMeeting.started_at)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDurationSeconds(Number(displayMeeting.total_duration_seconds ?? 0))}
                </span>
                {displayMeeting.organised_by ? (
                  <span>Organised by {displayMeeting.organised_by}</span>
                ) : null}
              </div>
              {attribution ? (
                <div className="mt-1 text-sm text-muted-foreground">{attribution}</div>
              ) : null}

              {showLanguagePills ? (
                <div
                  className="mt-3 inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 p-1"
                  role="tablist"
                  aria-label="Meeting language"
                >
                  {languagePills.map((pill) => (
                    <button
                      key={pill.code}
                      type="button"
                      role="tab"
                      aria-selected={pill.active}
                      disabled={isTranslating}
                      onClick={() => void selectLanguage(pill.code)}
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                        pill.active
                          ? "bg-foreground text-background"
                          : "text-muted-foreground hover:text-foreground",
                        isTranslating && "opacity-70",
                      )}
                    >
                      {pill.active && isTranslating ? (
                        <span className="inline-flex items-center gap-1.5">
                          <LoaderCircle className="h-3 w-3 animate-spin" />
                          {pill.label}
                        </span>
                      ) : (
                        pill.label
                      )}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {headerExtra}
              <MeetingRowActions
                meeting={displayMeeting}
                scope={scope}
                onDeleted={onBack}
                targetLanguage={selectedLanguage}
              />
            </div>
          </div>
        </GridBand>

        {isBusy ? (
          <GridBand className="flex items-center gap-2 bg-info-soft text-sm text-info">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            This meeting is still being processed — intelligence will appear once it&apos;s ready.
          </GridBand>
        ) : null}

        {resolvePanel}

        <MeetingScoreStrip metrics={displayMeeting.metrics} variant="cell" />

        {/* minmax(0,1fr) — a bare 1fr column has min-width:auto, so long
            transcript lines push past the container (08-07-2026). */}
        <GridCells className="grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <GridCell padded={false} className="min-w-0">
            {/* One row always (29-09-2026, DES): column count = visible tabs
                (6, or 7 when Agenda is on). Responsive multi-col wraps were
                stacking tabs; keep minmax(0,1fr) so long labels truncate. */}
            {/* Sticky tab bar (30-09-2026, DES): pins under the shell's
                quick-nav row like the menu. When the main header row slides
                away it moves up into that gap (the MLR format-bar pattern). */}
            <div
              className="sticky z-20 bg-background/90 backdrop-blur-md transition-[top] duration-300 ease-out supports-[backdrop-filter]:bg-background/75 motion-reduce:transition-none"
              style={{
                // `top`, not a transform: a transform also shifts the bar
                // while it is still in normal flow (30-09-2026).
                top: chromeCollapsed ? -headerHeight : 0,
              }}
            >
              <GridCells className={visibleTabs.length >= 7 ? "grid-cols-7" : "grid-cols-6"}>
                {visibleTabs.map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.id;
                  return (
                    <GridCell key={t.id} padded={false}>
                      <button
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={cn(
                          "flex w-full items-center justify-center gap-1 px-1.5 py-3 text-xs font-medium transition-colors sm:gap-1.5 sm:px-2 sm:text-sm",
                          active
                            ? "bg-brand-soft text-brand-soft-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        )}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                        <span className="truncate">{t.label}</span>
                      </button>
                    </GridCell>
                  );
                })}
              </GridCells>
            </div>

            {!isReady && tab !== "transcript" ? (
              <div className="p-4 md:p-6">
                <EmptyTab
                  icon={Sparkles}
                  title="Intelligence not ready"
                  hint="Summary, action items, decisions, and mindmap appear once synthesis completes."
                />
              </div>
            ) : tab === "transcript" ? (
              <TranscriptTab
                rows={transcriptRows}
                speakers={speakerMeta}
                totalDuration={Number(displayMeeting.total_duration_seconds ?? 0)}
                editable={
                  meeting.is_owner !== false &&
                  displayMeeting.status === "ready"
                }
                onEdit={(mode, selection) => setEdit({ mode, selection })}
                swap={speakerSwap}
              />
            ) : tab === "summary" ? (
              <SummaryTab
                meeting={displayMeeting}
                attachedNotes={attachedNotes ?? []}
                scope={scope}
                contentRtl={contentRtl}
              />
            ) : tab === "todos" ? (
              <TodosTab
                todos={displayMeeting.todos ?? []}
                speakers={speakerMeta}
                meeting={displayMeeting}
                contentRtl={contentRtl}
                readOnly={scope === "admin"}
              />
            ) : tab === "decisions" ? (
              <DecisionsTab meeting={displayMeeting} contentRtl={contentRtl} />
            ) : tab === "keypoints" ? (
              <KeyPointsTab meeting={displayMeeting} contentRtl={contentRtl} />
            ) : tab === "coverage" ? (
              <AgendaCoverageTab meeting={displayMeeting} contentRtl={contentRtl} />
            ) : (
              <div className="p-4 md:p-6">
                {tab === "mindmap" ? <MindmapTab meeting={displayMeeting} /> : null}
              </div>
            )}
          </GridCell>

          {/* Sticky rail (29-09-2026, DES): `self-start` so the cell is only
              as tall as the rail (not stretched-then-clipped), `sticky` so it
              pins for the full height of the left column. Inner sticky failed
              because the rail wrapper was short — sticky ran out of track.
              Same pattern as MLR's sticky side panel. */}
          <GridCell
            padded={false}
            className="sticky top-0 z-10 max-h-[calc(100vh-2rem)] self-start overflow-y-auto bg-background"
          >
            <DetailRail meeting={displayMeeting} />
          </GridCell>
        </GridCells>
      </GridBlock>

      <TranscriptEditSheet
        open={!!edit}
        onOpenChange={(open) => {
          if (!open) setEdit(null);
        }}
        mode={edit?.mode ?? "correct"}
        meeting={meeting}
        selection={edit?.selection ?? null}
        onDone={() => window.getSelection()?.removeAllRanges()}
      />
    </div>
  );
}
